import { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { DndContext, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getSlideContent, getDriveFileBasic } from './utils/googleDrive';
import {
  openGoogleSlidesPresentationPicker,
  openGoogleDriveFolderPicker,
  deferPickerOpen,
} from './utils/googleDrivePicker';
import {
  listPackFiles,
  createPackFile,
  savePack,
  savePackWithMeta,
  updatePackName,
  ensurePackForFolder,
  fileIsInFolder,
  moveFileIntoFolder,
  moveFileToFolder,
  syncPackWithFolder,
  unsetPackForFolder,
} from './utils/drivePacks';
import { getGooglePickerDeveloperKey } from './config/google';
import {
  ACCESS_TOKEN_REFRESH_MARGIN_MS,
  beginGoogleOAuthCodeFlow,
  renewAccessTokenForUser,
  encodeShareableAuthForQuery,
} from './utils/googleAuth';
import logo from './assets/logo.png';
import GoogleButton from './components/GoogleButton';
import OnboardingTour, { hasCompletedTour } from './components/OnboardingTour';
import {
  TourPlaceholderMain,
  TourPlaceholderSidebar,
  TourPlaceholderSlidesStrip,
} from './components/TourLayoutPlaceholders';
import AppDialog from './components/AppDialog';
import RemoteOperatorModal, {
  loadRemoteOperatorSession,
  saveRemoteOperatorSession,
} from './components/RemoteOperatorModal';
import FirefoxSlidesWarningBanner from './components/FirefoxSlidesWarningBanner';
import DualGoogleSlidesEmbed from './components/DualGoogleSlidesEmbed';
import { useFitTextToBox } from './hooks/useFitTextToBox';
import { useRemoteRelay } from './hooks/useRemoteRelay';
import { useRemoteJoinAlerts } from './hooks/useRemoteJoinAlerts';
import { remoteCreateRoom } from './utils/remoteApi';
import { useAppDialog } from './hooks/useAppDialog';
import { formatStopwatchSeconds } from './utils/stopwatchFormat';
import {
  AUDIENCE_AUDIO_FOCUS_KEY,
  readAudienceAudioFocusFromStorage,
} from './utils/audienceAudioFocus';
import { broadcastCurrentSlideState, DECKPACK_CURRENT_SLIDE_EVENT } from './utils/currentSlideBroadcast';
import {
  applyDeckSlideShortcutBinding,
  buildSlideJumpCodeIndex,
  formatShortcutBadgeLabel,
  getDeckSlideShortcutCode,
  isSlideShortcutBindingAllowed,
  mergeDeckSlideShortcutsPreferLocal,
  normalizeDeckSlideShortcuts,
} from './utils/slideShortcuts';
import './App.css';

/** Stable id for playlist drag-and-drop and row `data-id` (matches `playlistPositionId` when set). */
function playlistSortableId(slide, index) {
  if (slide?.playlistPositionId != null && slide.playlistPositionId !== '') {
    return String(slide.playlistPositionId);
  }
  return `slide-fallback-${index}-${slide?.id ?? 'row'}`;
}

/** Order packs by Drive folder name (numeric-aware: "2 Workshop" before "10 Closing"). */
function sortPacksByName(drivePacks) {
  if (!Array.isArray(drivePacks) || drivePacks.length === 0) return drivePacks || [];
  return [...drivePacks].sort((a, b) => {
    const na = String(a?.name ?? '').trim();
    const nb = String(b?.name ?? '').trim();
    return na.localeCompare(nb, undefined, { numeric: true, sensitivity: 'base' });
  });
}

const LAST_PACK_FOLDER_ID_KEY = 'deckpack-last-pack-folder-id';

function readLastPackFolderId() {
  try {
    const v = localStorage.getItem(LAST_PACK_FOLDER_ID_KEY);
    return v ? String(v) : '';
  } catch {
    return '';
  }
}

function writeLastPackFolderId(folderId) {
  try {
    if (folderId) localStorage.setItem(LAST_PACK_FOLDER_ID_KEY, String(folderId));
    else localStorage.removeItem(LAST_PACK_FOLDER_ID_KEY);
  } catch {
    /* ignore */
  }
}

function readPresenterTimerSync() {
  try {
    const raw = localStorage.getItem('presenter-timer-sync');
    if (!raw) return { elapsedSec: 0, running: false };
    const j = JSON.parse(raw);
    return {
      elapsedSec: typeof j.elapsedSec === 'number' ? j.elapsedSec : 0,
      running: Boolean(j.running),
    };
  } catch {
    return { elapsedSec: 0, running: false };
  }
}

/** Persist slide index + deck slide count so presenter/audience can detect “last slide” for playlist auto-advance. */
function persistCurrentSlideState(presentationId, index, slideContentMap) {
  if (!presentationId) return;
  const slides = slideContentMap?.[presentationId]?.slides;
  const slideCount = Array.isArray(slides) ? slides.length : 0;
  const payload = {
    presentationId,
    index,
    slideCount,
    timestamp: Date.now(),
  };
  try {
    localStorage.setItem('current-slide-state', JSON.stringify(payload));
    broadcastCurrentSlideState(payload);
  } catch {
    /* ignore */
  }
}

function packPersistencePayload(pack, patch = {}) {
  if (!pack?.id) return null;
  return {
    slides: patch.slides ?? pack.slides ?? [],
    removedFileIds: patch.removedFileIds ?? pack.removedFileIds ?? [],
    deckSlideShortcuts: normalizeDeckSlideShortcuts(
      patch.deckSlideShortcuts ?? pack.deckSlideShortcuts ?? {}
    ),
  };
}

function App() {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isScreenTooSmall, setIsScreenTooSmall] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  const [activeSlide, setActiveSlide] = useState(null);
  const activeSlideIdRef = useRef(null);
  activeSlideIdRef.current = activeSlide?.id ?? null;
  const [showDropdown, setShowDropdown] = useState(false);
  const [packs, setPacks] = useState([]);
  const [slideContent, setSlideContent] = useState({});
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  /** Notes + slide strip follow the visible iframe after load; `currentSlideIndex` updates immediately for nav logic. */
  const [chromeSlideIndex, setChromeSlideIndex] = useState(0);
  const currentSlideIndexRef = useRef(0);
  currentSlideIndexRef.current = currentSlideIndex;
  const [isPacksActionModalOpen, setIsPacksActionModalOpen] = useState(false);
  const [lastAddedDeckId, setLastAddedDeckId] = useState(null);
  const [addFeedback, setAddFeedback] = useState({
    show: false,
    message: '',
    type: '',
    deckId: null,
    deckToAdd: null
  });
  const [presentationWindow, setPresentationWindow] = useState(null);
  const [presenterWindow, setPresenterWindow] = useState(null);
  const [presenterViewOpenFromStorage, setPresenterViewOpenFromStorage] = useState(() => !!localStorage.getItem('presenter-view-open'));
  const [audienceViewOpenFromStorage, setAudienceViewOpenFromStorage] = useState(() => !!localStorage.getItem('audience-view-open'));
  /** When an audience window exists: only mute the console preview if that audience window is focused (owns audio). */
  const [audienceDeckHasFocus, setAudienceDeckHasFocus] = useState(() => readAudienceAudioFocusFromStorage());
  const [isBlackout, setIsBlackout] = useState(false);
  const [isWhiteout, setIsWhiteout] = useState(false);
  const [isPackLoading, setIsPackLoading] = useState(false);
  /** Deck file IDs still fetching Slides API data (thumbnails + notes); UI spinners only for the active deck */
  const [slideContentLoadingIds, setSlideContentLoadingIds] = useState(() => new Set());
  const [profileImageError, setProfileImageError] = useState(false);
  const [previewHeightRatio, setPreviewHeightRatio] = useState(0.7); // fraction of container
  const [isResizingPreview, setIsResizingPreview] = useState(false);
  /** `{ presentationId, objectId, slideNumber }` while listening for the next key to bind (or Backspace to clear). */
  const [slideShortcutCapture, setSlideShortcutCapture] = useState(null);
  // Initialize auto-advance from localStorage, default to false
  const [autoAdvance, setAutoAdvance] = useState(() => {
    const saved = localStorage.getItem('deckpack-auto-advance');
    return saved ? JSON.parse(saved) : false;
  });
  const autoAdvanceRef = useRef(autoAdvance);
  autoAdvanceRef.current = autoAdvance;

  // Folder browser state
  /** First-visit guided tour (per device via localStorage) */
  const [tourOpen, setTourOpen] = useState(false);
  const [remoteOperatorModalOpen, setRemoteOperatorModalOpen] = useState(false);
  const [remoteSession, setRemoteSession] = useState(() => loadRemoteOperatorSession());
  /** Shown in Remote modal when immediate start-from-icon fails (user never sees the idle “start” step on success). */
  const [remotePhoneStartError, setRemotePhoneStartError] = useState('');
  const [remotePendingCount, setRemotePendingCount] = useState(0);
  /** Bumps when join-alert prefs change in the modal so notification effect re-evaluates. */
  const [joinAlertPrefEpoch, setJoinAlertPrefEpoch] = useState(0);
  /** Stopwatch mirrored to remote when Presenter View is closed (see readPresenterTimerSync). */
  const [relayTimerSec, setRelayTimerSec] = useState(0);
  const [relayTimerRunning, setRelayTimerRunning] = useState(true);
  const relayTimerRunningRef = useRef(true);
  relayTimerRunningRef.current = relayTimerRunning;

  const handleRemoteOperatorSession = useCallback((s) => {
    setRemoteSession(s);
    saveRemoteOperatorSession(s);
    if (!s) setRemotePendingCount(0);
    else setRemotePhoneStartError('');
  }, []);

  const remoteModalOpenRef = useRef(false);
  remoteModalOpenRef.current = remoteOperatorModalOpen;

  const openRemoteModal = useCallback(() => {
    setRemotePhoneStartError('');
    setRemoteOperatorModalOpen(true);
  }, []);

  const handleRemotePhoneClick = useCallback(async () => {
    setRemotePhoneStartError('');
    const stored = loadRemoteOperatorSession();
    if (remoteSession?.roomId && remoteSession?.operatorSecret) {
      setRemoteOperatorModalOpen(true);
      return;
    }
    if (stored?.roomId && stored?.operatorSecret) {
      handleRemoteOperatorSession(stored);
      setRemoteOperatorModalOpen(true);
      return;
    }
    try {
      const data = await remoteCreateRoom();
      handleRemoteOperatorSession({
        roomId: data.roomId,
        operatorSecret: data.operatorSecret,
      });
      setRemoteOperatorModalOpen(true);
    } catch (e) {
      setRemotePhoneStartError(e?.message || 'Could not start remote session.');
      setRemoteOperatorModalOpen(true);
    }
  }, [remoteSession, handleRemoteOperatorSession]);

  useRemoteJoinAlerts({
    session: remoteSession,
    remoteModalOpenRef,
    onOpenRemoteModal: openRemoteModal,
    setPendingCount: setRemotePendingCount,
    joinAlertPrefEpoch,
  });

  useEffect(() => {
    const onWin = (e) => {
      if (e.data?.type !== 'deckpack-open-remote-modal') return;
      if (e.origin && e.origin !== window.location.origin) return;
      setRemotePhoneStartError('');
      setRemoteOperatorModalOpen(true);
    };
    const onSw = (e) => {
      if (e.data?.type === 'deckpack-open-remote-modal') {
        setRemotePhoneStartError('');
        setRemoteOperatorModalOpen(true);
      }
    };
    window.addEventListener('message', onWin);
    navigator.serviceWorker?.addEventListener?.('message', onSw);
    return () => {
      window.removeEventListener('message', onWin);
      navigator.serviceWorker?.removeEventListener?.('message', onSw);
    };
  }, []);

  const previewNotesRef = useRef(null);
  const presentationWindowRef = useRef(null);
  const presenterWindowRef = useRef(null);
  const notesSectionRef = useRef(null);
  const notesTextRef = useRef(null);
  const slideContentRef = useRef(slideContent);
  slideContentRef.current = slideContent;
  const selectedPackIdRef = useRef(selectedPack?.id);
  selectedPackIdRef.current = selectedPack?.id;
  const selectedPackRef = useRef(selectedPack);
  selectedPackRef.current = selectedPack;
  const userRef = useRef(user);
  userRef.current = user;
  const slideShortcutCaptureRef = useRef(null);
  slideShortcutCaptureRef.current = slideShortcutCapture;
  /** When assigning a slide key, focus leaves cross-origin iframes so `window` keydown handlers receive the press. */
  const slideShortcutCaptureFocusRef = useRef(null);
  /** Bumped when a playlist save supersedes an in-flight `handleSelectPack` sync so stale results cannot wipe adds. */
  const packLoadGenerationRef = useRef(0);
  const getRemoteSnapshotRef = useRef(() => null);
  const remoteCommandsRef = useRef(() => 0);
  /** Clears stale auto-hide timers so rapid blackout/whiteout toggles do not hide the latest toast. */
  const overlayNotifyHideTimeoutRef = useRef(null);
  /** Main preview embed — remote “Reveal” / keyboard nudge advance click-to-reveal builds. */
  const mainSlidesEmbedRef = useRef(null);
  /** Bumped so presenter / audience windows can mirror the same in-slide advance (storage event). */
  const bumpInSlideRevealAllWindowsRef = useRef(() => {});
  bumpInSlideRevealAllWindowsRef.current = () => {
    try {
      mainSlidesEmbedRef.current?.nudgeInSlideAdvance?.();
    } catch {
      /* ignore */
    }
    try {
      localStorage.setItem('deckpack-embed-reveal', String(Date.now()));
    } catch {
      /* ignore */
    }
  };

  // When presenter / audience bumps `deckpack-embed-reveal`, sync the control preview too.
  useEffect(() => {
    const onReveal = (e) => {
      if (e.key !== 'deckpack-embed-reveal' || !e.newValue) return;
      try {
        mainSlidesEmbedRef.current?.nudgeInSlideAdvance?.();
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('storage', onReveal);
    return () => window.removeEventListener('storage', onReveal);
  }, []);

  // Slides preview iframes steal keyboard focus; blur them when the user clicks elsewhere so Space/arrows work.
  useEffect(() => {
    const onPointerDownCapture = (e) => {
      const t = e.target;
      if (!t || typeof t.closest !== 'function') return;
      if (t.closest('iframe.slides-embed-pane')) return;
      if (t.tagName === 'IFRAME' && t.classList?.contains('slides-embed-pane')) return;
      try {
        mainSlidesEmbedRef.current?.blurEmbeds?.();
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('pointerdown', onPointerDownCapture, true);
    return () => window.removeEventListener('pointerdown', onPointerDownCapture, true);
  }, []);

  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: {
      distance: 5,
    },
  }));

  const [notification, setNotification] = useState({
    message: '',
    type: '',
    visible: false
  });

  const {
    dialog: appDialog,
    resolve: resolveAppDialog,
    confirm: appConfirm,
    alert: appAlert,
    prompt: appPrompt,
  } = useAppDialog();

  /** Full chrome with [data-tour] targets when touring before any pack is open */
  const tourLayoutPlaceholders = tourOpen && !selectedPack;

  const slideJumpCodeToObjectId = useMemo(
    () => buildSlideJumpCodeIndex(selectedPack?.deckSlideShortcuts, activeSlide?.id),
    [selectedPack?.deckSlideShortcuts, activeSlide?.id]
  );

  useEffect(() => {
    if (!slideShortcutCapture) return;
    if (String(activeSlide?.id || '') !== String(slideShortcutCapture.presentationId)) {
      setSlideShortcutCapture(null);
    }
  }, [activeSlide?.id, slideShortcutCapture]);

  useLayoutEffect(() => {
    if (!slideShortcutCapture) return undefined;
    try {
      mainSlidesEmbedRef.current?.blurEmbeds?.();
    } catch {
      /* ignore */
    }
    const id = window.requestAnimationFrame(() => {
      try {
        slideShortcutCaptureFocusRef.current?.focus({ preventScroll: true });
      } catch {
        /* ignore */
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [slideShortcutCapture]);

  const removePackFromFolder = useCallback(
    async (pack) => {
      if (!user?.access_token || !pack?.id) return;
      const ok = await appConfirm({
        title: 'Remove as Pack?',
        message: 'Stop treating this folder as a Pack? The folder and its contents will stay in Drive.',
        confirmLabel: 'Remove as Pack',
        cancelLabel: 'Cancel',
        danger: true,
      });
      if (!ok) return;
      try {
        const success = await unsetPackForFolder(user.access_token, pack.id);
        if (!success) {
          await appAlert({ title: "Couldn't remove pack", message: 'Please try again.' });
          return;
        }
        await new Promise((r) => setTimeout(r, 500));
        const updated = await listPackFiles(user.access_token);
        setPacks(sortPacksByName(updated));
        if (readLastPackFolderId() === String(pack.id)) {
          writeLastPackFolderId('');
        }
        if (selectedPack?.id === pack.id) {
          setSelectedPack(null);
          setActiveSlide(null);
        }
      } catch (error) {
        console.error('Error removing pack:', error);
        await appAlert({ title: "Couldn't remove pack", message: 'Please try again.' });
      }
    },
    [user?.access_token, appConfirm, appAlert, selectedPack?.id]
  );

  /** True only while the *active* playlist deck is still loading (others stay usable). */
  const activeSlideContentLoading = Boolean(
    activeSlide?.id && slideContentLoadingIds.has(activeSlide.id)
  );

  const deckIdsKey = useMemo(
    () => (selectedPack?.slides ?? []).map((s) => s.id).filter(Boolean).join(','),
    [selectedPack?.slides]
  );

  const previewSlidesSrc = useMemo(() => {
    if (!activeSlide?.id) return '';
    return `https://docs.google.com/presentation/d/${activeSlide.id}/preview?rm=minimal&delayms=0&slide=${currentSlideIndex + 1}`;
  }, [activeSlide?.id, currentSlideIndex]);

  const onMainEmbedVisibleReady = useCallback(() => {
    setChromeSlideIndex(currentSlideIndexRef.current);
  }, []);

  // Keep notes + slide-strip “active” highlight aligned with `currentSlideIndex` on every in-deck nav
  // (not only when the deck id changes — otherwise cross-tab / thumbnail UI lags the main preview).
  useEffect(() => {
    if (!activeSlide?.id) return;
    setChromeSlideIndex(currentSlideIndex);
  }, [activeSlide?.id, currentSlideIndex]);

  const slidesPreviewScrollRef = useRef(null);
  const activeDeckSlideCount = slideContent[activeSlide?.id]?.slides?.length ?? 0;

  /** Keep the active thumbnail in view (main preview, remote, presenter, or strip click — not only index ≥ 3). */
  useLayoutEffect(() => {
    if (tourLayoutPlaceholders) return;
    const root = slidesPreviewScrollRef.current;
    if (!root || !activeSlide?.id || activeSlideContentLoading) return;
    if (!activeDeckSlideCount) return;
    const el = root.querySelector('.slide-preview-item.active');
    if (!el || !(el instanceof HTMLElement)) return;
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [
    chromeSlideIndex,
    activeSlide?.id,
    activeSlideContentLoading,
    tourLayoutPlaceholders,
    activeDeckSlideCount,
  ]);

  const handleSlideChange = useCallback((index) => {
    if (!activeSlide || !activeSlide.id) return;

    setCurrentSlideIndex(index);

    persistCurrentSlideState(activeSlide.id, index, slideContent);
    queueMicrotask(() => {
      try {
        mainSlidesEmbedRef.current?.blurEmbeds?.();
      } catch {
        /* ignore */
      }
    });
  }, [activeSlide, slideContent]);

  /** Apply `current-slide-state` from another tab (same deck index change, or playlist deck switch). */
  const applyExternalCurrentSlideFromStorageRef = useRef(() => {});
  useEffect(() => {
    applyExternalCurrentSlideFromStorageRef.current = (pid, idx) => {
      if (!pid) return;
      if (String(pid) === String(activeSlideIdRef.current || '')) {
        handleSlideChange(idx);
        return;
      }
      const pack = selectedPackRef.current;
      const match = (pack?.slides || []).find((s) => String(s.id) === String(pid));
      if (!match?.id) return;
      setActiveSlide(match);
      setCurrentSlideIndex(idx);
      persistCurrentSlideState(match.id, idx, slideContentRef.current);
    };
  }, [activeSlide?.id, handleSlideChange]);

  // Mirror playlist order to localStorage so presenter/audience can advance packs with auto-advance.
  useEffect(() => {
    try {
      const slides = selectedPack?.slides;
      if (!slides?.length) {
        localStorage.removeItem('deckpack-playlist-context');
        return;
      }
      localStorage.setItem(
        'deckpack-playlist-context',
        JSON.stringify({
          packId: String(selectedPack.id),
          slides: slides
            .filter((s) => s?.id)
            .map((s) => ({
              id: String(s.id),
              playlistPositionId: s.playlistPositionId,
              name: s.name || '',
            })),
          ts: Date.now(),
        })
      );
    } catch {
      /* ignore */
    }
  }, [selectedPack]);

  // Refresh `slideCount` in localStorage when Slides metadata finishes loading (no index change).
  useEffect(() => {
    if (!activeSlide?.id) return;
    let prevCount = -1;
    try {
      const raw = localStorage.getItem('current-slide-state');
      if (!raw) return;
      const s = JSON.parse(raw);
      if (String(s.presentationId) !== String(activeSlide.id)) return;
      if (typeof s.index !== 'number' || s.index !== currentSlideIndex) return;
      prevCount = typeof s.slideCount === 'number' ? s.slideCount : -1;
    } catch {
      return;
    }
    const nextCount = slideContent[activeSlide.id]?.slides?.length ?? 0;
    if (nextCount <= 0 || prevCount === nextCount) return;
    persistCurrentSlideState(activeSlide.id, currentSlideIndex, slideContent);
  }, [activeSlide?.id, currentSlideIndex, slideContent]);

  // When the presenter (or another tab) updates `current-slide-state`, mirror it on the console.
  useEffect(() => {
    const applyFromState = (state) => {
      const pid = state?.presentationId;
      const idx = typeof state?.index === 'number' ? state.index : 0;
      if (!pid) return;
      applyExternalCurrentSlideFromStorageRef.current(pid, idx);
    };
    const onStorage = (e) => {
      if (e.key !== 'current-slide-state' || !e.newValue) return;
      try {
        applyFromState(JSON.parse(e.newValue));
      } catch {
        /* ignore */
      }
    };
    const onSameTabBroadcast = (ev) => {
      if (ev?.detail) applyFromState(ev.detail);
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(DECKPACK_CURRENT_SLIDE_EVENT, onSameTabBroadcast);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(DECKPACK_CURRENT_SLIDE_EVENT, onSameTabBroadcast);
    };
  }, []);

  const remoteGoPrevSlide = useCallback(() => {
    if (!activeSlide?.id) return;
    if (currentSlideIndex <= 0) return;
    handleSlideChange(currentSlideIndex - 1);
  }, [activeSlide?.id, currentSlideIndex, handleSlideChange]);

  const remoteGoNextSlide = useCallback(() => {
    if (!activeSlide?.id) return;
    const slides = slideContentRef.current[activeSlide.id]?.slides;
    const totalSlides = Array.isArray(slides) ? slides.length : 0;
    if (totalSlides > 0) {
      if (currentSlideIndex < totalSlides - 1) {
        handleSlideChange(currentSlideIndex + 1);
        return;
      }
      if (autoAdvance && selectedPack?.slides?.length) {
        const playlistIndex = selectedPack.slides.findIndex(
          (s) => s.playlistPositionId === activeSlide.playlistPositionId
        );
        if (playlistIndex >= 0 && playlistIndex < selectedPack.slides.length - 1) {
          const nextDeck = selectedPack.slides[playlistIndex + 1];
          if (nextDeck?.id) {
            setActiveSlide(nextDeck);
            setCurrentSlideIndex(0);
            const now = Date.now();
            persistCurrentSlideState(nextDeck.id, 0, slideContentRef.current);
            localStorage.setItem('active-presentation', JSON.stringify({
              presentationId: nextDeck.id,
              playlistPositionId: nextDeck.playlistPositionId,
              name: nextDeck.name,
              timestamp: now
            }));
          }
        }
      }
      return;
    }
    // Slides API not ready yet (or empty): still advance using persisted slideCount when available.
    let cap = 0;
    try {
      const raw = localStorage.getItem('current-slide-state');
      const st = raw ? JSON.parse(raw) : {};
      if (String(st.presentationId) === String(activeSlide.id) && typeof st.slideCount === 'number') {
        cap = st.slideCount;
      }
    } catch {
      /* ignore */
    }
    if (cap > 0) {
      if (currentSlideIndex < cap - 1) {
        handleSlideChange(currentSlideIndex + 1);
        return;
      }
      if (currentSlideIndex >= cap - 1 && autoAdvance && selectedPack?.slides?.length) {
        const playlistIndex = selectedPack.slides.findIndex(
          (s) => s.playlistPositionId === activeSlide.playlistPositionId
        );
        if (playlistIndex >= 0 && playlistIndex < selectedPack.slides.length - 1) {
          const nextDeck = selectedPack.slides[playlistIndex + 1];
          if (nextDeck?.id) {
            setActiveSlide(nextDeck);
            setCurrentSlideIndex(0);
            const now = Date.now();
            persistCurrentSlideState(nextDeck.id, 0, slideContentRef.current);
            localStorage.setItem('active-presentation', JSON.stringify({
              presentationId: nextDeck.id,
              playlistPositionId: nextDeck.playlistPositionId,
              name: nextDeck.name,
              timestamp: now
            }));
          }
        }
      }
      return;
    }
    handleSlideChange(currentSlideIndex + 1);
  }, [activeSlide, currentSlideIndex, handleSlideChange, autoAdvance, selectedPack]);

  const handleSelectPack = useCallback(async (packId, options) => {
    if (!user?.access_token) return;
    const idStr = String(packId);
    packLoadGenerationRef.current += 1;
    const loadGen = packLoadGenerationRef.current;
    setIsPackLoading(true);
    setActiveSlide(null);
    setCurrentSlideIndex(0);
    setSlideContent({});

    try {
      const result = await syncPackWithFolder(user.access_token, idStr);
      if (loadGen !== packLoadGenerationRef.current) {
        return;
      }
      const syncedSlides = result?.slides ?? result ?? [];
      const slidesWithIds = (Array.isArray(syncedSlides) ? syncedSlides : []).map((slide, index) => {
        if (!slide.playlistPositionId) {
          return {
            ...slide,
            playlistPositionId: Date.now() + index
          };
        }
        return slide;
      });
      const removedFileIds = result?.removedFileIds ?? [];

      setSelectedPack({
        id: idStr,
        name:
          (options && options.displayName) ||
          packs.find((p) => String(p.id) === idStr)?.name ||
          '',
        slides: slidesWithIds,
        removedFileIds,
        deckSlideShortcuts: normalizeDeckSlideShortcuts(result?.deckSlideShortcuts ?? {}),
      });
      writeLastPackFolderId(idStr);
      if (slidesWithIds.length > 0) {
        const first = slidesWithIds[0];
        setActiveSlide(first);
        setCurrentSlideIndex(0);
        if (first.id) {
          persistCurrentSlideState(first.id, 0, {});
          localStorage.setItem('active-presentation', JSON.stringify({
            presentationId: first.id,
            playlistPositionId: first.playlistPositionId,
            name: first.name,
            timestamp: Date.now()
          }));
        }
      }
    } finally {
      setIsPackLoading(false);
    }
  }, [user?.access_token, packs]);

  // Fit notes text to box (no scroll): measure and set font size so content fits
  // Include loading flag so fit runs after notes mount (ref is absent while placeholder shows)
  useFitTextToBox(
    notesSectionRef,
    notesTextRef,
    `${activeSlide?.id ?? ''}-${chromeSlideIndex}-${activeSlideContentLoading ? 'loading' : 'ready'}`
  );

  // Basic guardrail for very small viewports
  useEffect(() => {
    const checkSize = () => {
      const w = window.innerWidth;
      // Allow most laptop/tablet sizes, only block truly narrow viewports.
      // We only gate on width now to avoid false positives when height is reduced
      // by browser chrome or devtools.
      setIsScreenTooSmall(w < 900);
    };

    checkSize();
    window.addEventListener('resize', checkSize);
    return () => window.removeEventListener('resize', checkSize);
  }, []);

  // First-time onboarding tour (skipped if already completed on this device)
  useEffect(() => {
    if (!isAuthenticated || isScreenTooSmall) return;
    if (hasCompletedTour()) return;
    const t = setTimeout(() => setTourOpen(true), 700);
    return () => clearTimeout(t);
  }, [isAuthenticated, isScreenTooSmall]);

  // Keep refs in sync with state
  useEffect(() => {
    presentationWindowRef.current = presentationWindow;
  }, [presentationWindow]);

  useEffect(() => {
    presenterWindowRef.current = presenterWindow;
  }, [presenterWindow]);

  // Global listener for auth expiry flagged by API helpers.
  // First try a silent re-auth; only log out if that fails.
  // Keep session active if presentation/presenter windows are open.
  useEffect(() => {
    const silentReauth = async () => {
      const savedAuth = localStorage.getItem('user_auth');
      const base = savedAuth ? JSON.parse(savedAuth) : {};
      const userAuth = await renewAccessTokenForUser(base);
      setUser(userAuth);
      setIsAuthenticated(true);
      localStorage.setItem('user_auth', JSON.stringify(userAuth));
    };

    const handleAuthExpired = async () => {
      const flag = localStorage.getItem('deckpack_auth_expired');
      if (!flag) return;
      localStorage.removeItem('deckpack_auth_expired');

      // Check if presentation or presenter windows are still open using refs
      // to always get the current window objects
      const hasActivePresentation = 
        (presentationWindowRef.current && !presentationWindowRef.current.closed) ||
        (presenterWindowRef.current && !presenterWindowRef.current.closed);

      try {
        await silentReauth();
      } catch {
        // If silent re-auth failed but presentation windows are active,
        // keep retrying instead of logging out
        if (hasActivePresentation) {
          // Retry after a delay (exponential backoff)
          setTimeout(() => {
            localStorage.setItem('deckpack_auth_expired', Date.now().toString());
            handleAuthExpired();
          }, 5000); // Retry after 5 seconds
        } else {
          // No active presentations, safe to log out
          // Use state setters directly instead of handleLogout to avoid scope issues
          setUser(null);
          setIsAuthenticated(false);
          setShowDropdown(false);
          setSelectedPack(null);
          setActiveSlide(null);
          localStorage.removeItem("user_auth");
          localStorage.setItem('app-session-state', JSON.stringify({
            action: 'logout',
            timestamp: Date.now()
          }));
        }
      }
    };

    handleAuthExpired();

    const onStorage = (e) => {
      if (e.key === 'deckpack_auth_expired' && e.newValue) {
        handleAuthExpired();
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Add these control functions
  const handleTimerReset = useCallback(() => {
    localStorage.setItem('presenter-timer-state', JSON.stringify({
      reset: true,
      timestamp: Date.now()
    }));
    setRelayTimerSec(0);
    setRelayTimerRunning(true);
    relayTimerRunningRef.current = true;
    try {
      localStorage.setItem(
        'presenter-timer-sync',
        JSON.stringify({ elapsedSec: 0, running: true, ts: Date.now() })
      );
    } catch {
      /* ignore */
    }
    setNotification({
      message: 'Timer Reset',
      type: 'info',
      visible: true
    });
    setTimeout(() => {
      setNotification(prev => ({...prev, visible: false}));
    }, 2000);
  }, []);

  const blackoutRef = useRef(isBlackout);
  blackoutRef.current = isBlackout;
  const whiteoutRef = useRef(isWhiteout);
  whiteoutRef.current = isWhiteout;

  const clearOverlayNotifyHideTimeout = useCallback(() => {
    if (overlayNotifyHideTimeoutRef.current != null) {
      clearTimeout(overlayNotifyHideTimeoutRef.current);
      overlayNotifyHideTimeoutRef.current = null;
    }
  }, []);

  const handleBlackout = useCallback(() => {
    const next = !blackoutRef.current;
    setIsWhiteout(false);
    setIsBlackout(next);
    localStorage.setItem(
      'presentation-overlay',
      JSON.stringify({ type: next ? 'black' : 'none', timestamp: Date.now() })
    );
    clearOverlayNotifyHideTimeout();
    setNotification({
      message: next ? 'Blackout Active' : 'Blackout Disabled',
      type: next ? 'active' : 'info',
      visible: true,
    });
    if (!next) {
      overlayNotifyHideTimeoutRef.current = window.setTimeout(() => {
        overlayNotifyHideTimeoutRef.current = null;
        setNotification((prev) => ({ ...prev, visible: false }));
      }, 2000);
    }
  }, [clearOverlayNotifyHideTimeout]);

  const handleWhiteout = useCallback(() => {
    const next = !whiteoutRef.current;
    setIsBlackout(false);
    setIsWhiteout(next);
    localStorage.setItem(
      'presentation-overlay',
      JSON.stringify({ type: next ? 'white' : 'none', timestamp: Date.now() })
    );
    clearOverlayNotifyHideTimeout();
    setNotification({
      message: next ? 'Whiteout Active' : 'Whiteout Disabled',
      type: next ? 'active' : 'info',
      visible: true,
    });
    if (!next) {
      overlayNotifyHideTimeoutRef.current = window.setTimeout(() => {
        overlayNotifyHideTimeoutRef.current = null;
        setNotification((prev) => ({ ...prev, visible: false }));
      }, 2000);
    }
  }, [clearOverlayNotifyHideTimeout]);

  const clearPresentationOverlay = useCallback(() => {
    setIsBlackout(false);
    setIsWhiteout(false);
    localStorage.setItem('presentation-overlay', JSON.stringify({
      type: 'none',
      timestamp: Date.now()
    }));
    clearOverlayNotifyHideTimeout();
    setNotification((prev) => ({ ...prev, visible: false }));
  }, [clearOverlayNotifyHideTimeout]);

  const remoteGoNextSlideRef = useRef(remoteGoNextSlide);
  const remoteGoPrevSlideRef = useRef(remoteGoPrevSlide);
  const handleTimerResetRef = useRef(handleTimerReset);
  const handleBlackoutRef = useRef(handleBlackout);
  const handleWhiteoutRef = useRef(handleWhiteout);
  const clearPresentationOverlayRef = useRef(clearPresentationOverlay);

  useEffect(() => {
    remoteGoNextSlideRef.current = remoteGoNextSlide;
    remoteGoPrevSlideRef.current = remoteGoPrevSlide;
    handleTimerResetRef.current = handleTimerReset;
    handleBlackoutRef.current = handleBlackout;
    handleWhiteoutRef.current = handleWhiteout;
    clearPresentationOverlayRef.current = clearPresentationOverlay;
  }, [
    remoteGoNextSlide,
    remoteGoPrevSlide,
    handleTimerReset,
    handleBlackout,
    handleWhiteout,
    clearPresentationOverlay,
  ]);

  useEffect(() => {
    if (!activeSlide?.id) return;
    setRelayTimerSec(0);
    setRelayTimerRunning(true);
    relayTimerRunningRef.current = true;
  }, [activeSlide?.id]);

  useEffect(() => {
    if (!remoteSession?.roomId || !activeSlide?.id) return undefined;
    const id = window.setInterval(() => {
      try {
        if (localStorage.getItem('presenter-view-open')) {
          const s = readPresenterTimerSync();
          setRelayTimerSec(s.elapsedSec);
          setRelayTimerRunning(s.running);
          relayTimerRunningRef.current = s.running;
          return;
        }
        if (!relayTimerRunningRef.current) return;
        setRelayTimerSec((prev) => {
          const next = prev + 1;
          try {
            localStorage.setItem(
              'presenter-timer-sync',
              JSON.stringify({ elapsedSec: next, running: true, ts: Date.now() })
            );
          } catch {
            /* ignore */
          }
          return next;
        });
      } catch {
        /* ignore */
      }
    }, 1000);
    return () => clearInterval(id);
  }, [remoteSession?.roomId, activeSlide?.id]);

  useEffect(() => {
    getRemoteSnapshotRef.current = () => {
      if (!activeSlide?.id) return null;
      const deckContent = slideContent[activeSlide.id];
      const slides = deckContent?.slides || [];
      const idx = currentSlideIndex;
      const notesHtml = slides[idx]?.notes || ' ';
      const prevThumb = idx > 0 ? slides[idx - 1]?.thumbnail : null;
      const nextThumb = idx < slides.length - 1 ? slides[idx + 1]?.thumbnail : null;
      const overlay = isBlackout ? 'black' : isWhiteout ? 'white' : 'none';
      const presenterOpen = Boolean(
        typeof localStorage !== 'undefined' && localStorage.getItem('presenter-view-open')
      );
      const timer = presenterOpen
        ? readPresenterTimerSync()
        : { elapsedSec: relayTimerSec, running: relayTimerRunning };
      const timerLabel = formatStopwatchSeconds(timer.elapsedSec);
      return {
        presentationId: activeSlide.id,
        slideIndex: idx,
        slideCount: slides.length,
        notesHtml,
        timerSec: timer.elapsedSec,
        timerRunning: timer.running,
        timerLabel,
        overlay,
        autoAdvance,
        prevThumb,
        nextThumb,
        currentThumb: slides[idx]?.thumbnail ?? null,
        deckName: activeSlide.name || '',
      };
    };
  }, [
    activeSlide,
    slideContent,
    currentSlideIndex,
    isBlackout,
    isWhiteout,
    autoAdvance,
    relayTimerSec,
    relayTimerRunning,
  ]);

  useEffect(() => {
    remoteCommandsRef.current = (commands) => {
      let maxId = 0;
      let navAccum = 0;
      /**
       * Same remote occasionally delivers two identical nav commands in one inbox batch (e.g.
       * Space on a focused “Next” button: window key handler + native button activation). Drop
       * consecutive duplicates from the same client within a short window so we advance one slide.
       */
      const deduped = [];
      for (let i = 0; i < commands.length; i += 1) {
        const c = commands[i];
        const prev = deduped[deduped.length - 1];
        const dupWindowMs = 140;
        if (
          prev &&
          (c.action === 'next' || c.action === 'prev') &&
          c.action === prev.action &&
          c.remoteId &&
          c.remoteId === prev.remoteId &&
          typeof c.at === 'number' &&
          typeof prev.at === 'number' &&
          c.at - prev.at >= 0 &&
          c.at - prev.at < dupWindowMs
        ) {
          maxId = Math.max(maxId, c.id);
          continue;
        }
        deduped.push(c);
      }
      const applyNavNow = () => {
        if (navAccum > 0) {
          if (navAccum === 1) {
            bumpInSlideRevealAllWindowsRef.current();
            remoteGoNextSlideRef.current();
          } else {
            for (let i = 0; i < navAccum; i += 1) {
              flushSync(() => {
                bumpInSlideRevealAllWindowsRef.current();
                remoteGoNextSlideRef.current();
              });
            }
          }
        } else if (navAccum < 0) {
          if (navAccum === -1) {
            remoteGoPrevSlideRef.current();
          } else {
            for (let i = 0; i < -navAccum; i += 1) {
              flushSync(() => {
                remoteGoPrevSlideRef.current();
              });
            }
          }
        }
        navAccum = 0;
      };
      for (const c of deduped) {
        maxId = Math.max(maxId, c.id);
        if (c.action === 'reveal' || c.action === 'advance_build') {
          applyNavNow();
          bumpInSlideRevealAllWindowsRef.current();
          remoteGoNextSlideRef.current();
          continue;
        }
        if (c.action === 'next') {
          applyNavNow();
          bumpInSlideRevealAllWindowsRef.current();
          remoteGoNextSlideRef.current();
          continue;
        }
        if (c.action === 'prev') {
          navAccum -= 1;
          continue;
        }
        applyNavNow();
        switch (c.action) {
          case 'timer_reset':
            handleTimerResetRef.current();
            break;
          case 'blackout':
            handleBlackoutRef.current();
            break;
          case 'whiteout':
            handleWhiteoutRef.current();
            break;
          case 'overlay_none':
            clearPresentationOverlayRef.current();
            break;
          default:
            break;
        }
      }
      applyNavNow();
      return maxId;
    };
  }, []);

  const { peerCount, broadcastSessionEnd, disconnectPeer, sendApproval } = useRemoteRelay({
    enabled: Boolean(
      remoteSession?.roomId && remoteSession?.operatorSecret && isAuthenticated
    ),
    roomId: remoteSession?.roomId,
    operatorSecret: remoteSession?.operatorSecret,
    getSnapshotRef: getRemoteSnapshotRef,
    onCommandsRef: remoteCommandsRef,
  });

  // Check if presenter window is still open
  useEffect(() => {
    if (!presenterWindow) return;
    
    const checkWindow = setInterval(() => {
      if (presenterWindow.closed) {
        setPresenterWindow(null);
      }
    }, 1000);
    
    return () => clearInterval(checkWindow);
  }, [presenterWindow]);

  // Sync clock button with presenter view when we don't have window ref (e.g. after control-panel refresh)
  useEffect(() => {
    const check = () => setPresenterViewOpenFromStorage(!!localStorage.getItem('presenter-view-open'));
    const interval = setInterval(check, 1000);
    const onStorage = (e) => {
      if (e.key === 'presenter-view-open') check();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Audience View: sync open flag + which audience window owns deck audio (focus key).
  useEffect(() => {
    const syncFocus = () => {
      setAudienceDeckHasFocus(readAudienceAudioFocusFromStorage());
    };
    const check = () => {
      if (presentationWindow && presentationWindow.closed) {
        try {
          localStorage.removeItem('audience-view-open');
          localStorage.removeItem(AUDIENCE_AUDIO_FOCUS_KEY);
        } catch {
          /* ignore */
        }
        setPresentationWindow(null);
      }
      const audOpen = !!localStorage.getItem('audience-view-open');
      setAudienceViewOpenFromStorage(audOpen);
      if (!audOpen) {
        try {
          localStorage.removeItem(AUDIENCE_AUDIO_FOCUS_KEY);
        } catch {
          /* ignore */
        }
      }
      syncFocus();
    };
    const interval = setInterval(check, 400);
    const onStorage = (e) => {
      if (e.key === 'audience-view-open' || e.key === AUDIENCE_AUDIO_FOCUS_KEY) {
        check();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', onStorage);
    };
  }, [presentationWindow]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only respond to key events when user is not typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (slideShortcutCaptureRef.current) return;
      
      if (e.key.toLowerCase() === 't') {
        const presenterOpen = (presenterWindow && !presenterWindow.closed) || presenterViewOpenFromStorage;
        if (presenterOpen) handleTimerReset();
      } else if (e.key.toLowerCase() === 'b') {
        handleBlackout();
      } else if (e.key.toLowerCase() === 'w') {
        handleWhiteout();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleTimerReset, handleBlackout, handleWhiteout, presenterWindow, presenterViewOpenFromStorage]);

  // Auth check on initial load
  useEffect(() => {
    document.title = "Deck Pack - Control Panel";
    const savedAuth = localStorage.getItem("user_auth");
    if (savedAuth) {
      const userAuth = JSON.parse(savedAuth);
      if (userAuth.expiry > Date.now()) {
        // Old sessions without access token expiry: refresh soon (silent).
        if (typeof userAuth.accessTokenExpiresAt !== 'number') {
          userAuth.accessTokenExpiresAt = Date.now() + 5 * 60 * 1000;
        }
        setUser(userAuth);
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem("user_auth");
        setUser(null);
        setIsAuthenticated(false);
      }
    } else {
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  // Proactively refresh the Google access token before it expires (~1h) so Drive/Slides
  // calls don't fail mid-presentation. No UI — silent only.
  useEffect(() => {
    if (!isAuthenticated || !user?.access_token) return;

    let expiresAt = user.accessTokenExpiresAt;
    if (typeof expiresAt !== 'number' || Number.isNaN(expiresAt)) {
      expiresAt = Date.now() + 5 * 60 * 1000;
    }

    const delayMs = Math.max(
      1000,
      expiresAt - Date.now() - ACCESS_TOKEN_REFRESH_MARGIN_MS
    );

    const timeoutId = setTimeout(() => {
      const raw = localStorage.getItem('user_auth');
      const current = raw ? JSON.parse(raw) : user;
      renewAccessTokenForUser(current)
        .then((next) => {
          setUser((prev) => {
            if (!prev?.access_token) return prev;
            localStorage.setItem('user_auth', JSON.stringify(next));
            return next;
          });
        })
        .catch((err) => {
          console.warn('Proactive token refresh failed', err);
        });
    }, delayMs);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- timer keyed on token/expiry/refresh only; omit full `user` to avoid resetting on unrelated updates
  }, [isAuthenticated, user?.access_token, user?.accessTokenExpiresAt, user?.refresh_token]);

  // Sync auto-advance state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('deckpack-auto-advance', JSON.stringify(autoAdvance));
  }, [autoAdvance]);

  // Load packs when user is authenticated (playlist decks are added via Google Picker).
  useEffect(() => {
    if (user?.access_token) {
      listPackFiles(user.access_token).then((list) => setPacks(sortPacksByName(list)));
    }
  }, [user]);

  // If packs already exist, auto-select the last-used pack (folder id) when still present; else first pack.
  useEffect(() => {
    if (user?.access_token && packs.length > 0 && !selectedPack) {
      const savedId = readLastPackFolderId();
      const fromSaved = savedId ? packs.find((p) => String(p.id) === savedId) : null;
      const target = fromSaved || packs[0];
      setSelectedPack({
        id: target.id,
        name: target.name,
        slides: [],
        deckSlideShortcuts: {},
      });
      void handleSelectPack(target.id);
    }
  }, [user?.access_token, packs, selectedPack, handleSelectPack]);

  // Background sync for the currently selected Pack so Drive changes are picked up automatically
  useEffect(() => {
    if (!user?.access_token || !selectedPack?.id) return;

    const interval = setInterval(async () => {
      try {
        const result = await syncPackWithFolder(user.access_token, selectedPack.id);
        const syncedSlides = result?.slides ?? result ?? [];
        const slidesWithIds = (Array.isArray(syncedSlides) ? syncedSlides : []).map((slide, index) =>
          slide.playlistPositionId
            ? slide
            : { ...slide, playlistPositionId: Date.now() + index }
        );
        const removedFileIds = result?.removedFileIds ?? [];

        setSelectedPack((prev) =>
          prev && prev.id === selectedPack.id
            ? {
                ...prev,
                slides: slidesWithIds,
                removedFileIds,
                deckSlideShortcuts: mergeDeckSlideShortcutsPreferLocal(
                  prev.deckSlideShortcuts,
                  result?.deckSlideShortcuts ?? {}
                ),
              }
            : prev
        );
      } catch (err) {
        console.error('Background Pack sync failed', err);
      }
    }, 30000); // every 30 seconds

    return () => clearInterval(interval);
  }, [user?.access_token, selectedPack?.id]);

  // Load slide content per deck as needed; merge as each completes (playlist stays usable).
  useEffect(() => {
    if (!selectedPack?.slides?.length || !user?.access_token) {
      setSlideContentLoadingIds(new Set());
      return;
    }
    const packId = selectedPack.id;
    const decks = selectedPack.slides.filter((d) => d?.id);
    if (decks.length === 0) {
      setSlideContentLoadingIds(new Set());
      return;
    }

    const prev = slideContentRef.current;
    const idsToFetch = decks.map((d) => d.id).filter((id) => prev[id] == null);
    const packIdSet = new Set(decks.map((d) => d.id));

    setSlideContentLoadingIds((prevIds) => {
      const next = new Set(prevIds);
      // Drop decks that left the playlist or the whole pack changed
      for (const id of next) {
        if (!packIdSet.has(id)) next.delete(id);
      }
      idsToFetch.forEach((id) => next.add(id));
      return next;
    });

    if (idsToFetch.length === 0) {
      return;
    }

    idsToFetch.forEach((id) => {
      getSlideContent(user.access_token, id)
        .then((data) => {
          if (selectedPackIdRef.current !== packId) return;
          setSlideContent((p) => {
            if (p[id] != null) return p;
            return { ...p, [id]: data };
          });
        })
        .catch((err) => {
          console.error('Failed to load slide content for deck', id, err);
          if (selectedPackIdRef.current !== packId) return;
          setSlideContent((p) => {
            if (p[id] != null) return p;
            return { ...p, [id]: { slides: [] } };
          });
        })
        .finally(() => {
          setSlideContentLoadingIds((s) => {
            const next = new Set(s);
            next.delete(id);
            return next;
          });
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deckIdsKey mirrors selectedPack.slides ids; listing slides would duplicate deps
  }, [selectedPack?.id, deckIdsKey, user?.access_token]);

  useEffect(() => {
    if (activeSlide?.id && slideContent[activeSlide.id]) {
      localStorage.setItem(
        `slide-content-${activeSlide.id}`,
        JSON.stringify(slideContent[activeSlide.id])
      );
    }
  }, [activeSlide, slideContent]);

  const [isSyncing, setIsSyncing] = useState(false);

  const refreshSlideContent = useCallback(async () => {
    if (!selectedPack?.slides?.length || !user?.access_token) return;
    const packId = selectedPack.id;
    const deckIds = selectedPack.slides.filter((d) => d?.id).map((d) => d.id);
    if (deckIds.length === 0) return;

    for (const id of deckIds) {
      if (selectedPackIdRef.current !== packId) return;
      try {
        const data = await getSlideContent(user.access_token, id, { skipCache: true });
        if (selectedPackIdRef.current !== packId) return;
        setSlideContent((prev) => {
          const existing = prev[id];
          if (!existing) return { ...prev, [id]: data };
          if (
            existing.slides?.length === data.slides?.length &&
            existing.slides?.every((s, i) => {
              const ns = data.slides[i];
              return s.objectId === ns.objectId && s.notes === ns.notes && s.thumbnail === ns.thumbnail;
            })
          ) return prev;
          return { ...prev, [id]: data };
        });
      } catch { /* ignore transient errors */ }
    }
  }, [selectedPack?.id, selectedPack?.slides, user?.access_token]);

  const handleManualResync = useCallback(async () => {
    setIsSyncing(true);
    try {
      await refreshSlideContent();
    } finally {
      setIsSyncing(false);
    }
  }, [refreshSlideContent]);

  useEffect(() => {
    if (!selectedPack?.slides?.length || !user?.access_token) return undefined;
    const t = window.setInterval(refreshSlideContent, 30000);
    return () => clearInterval(t);
  }, [selectedPack?.slides, user?.access_token, refreshSlideContent]);

  // Keyboard: forward keys = cross-tab bump + advance slide index (all windows stay on the same slide URL).
  // In-slide click-to-reveal inside Google’s iframe is not reliable from the parent; sync beats per-build fidelity.
  useEffect(() => {
    /** Only skip deck keys for real text entry or an open modal — not generic buttons (strip/sidebar). */
    const shouldDeferDeckKeys = (el) =>
      el &&
      typeof el.closest === 'function' &&
      (el.closest('input, textarea, select, [contenteditable="true"]') ||
        el.closest('.modal-overlay, .app-dialog-overlay'));

    const handleKeyPress = (e) => {
      const cap = slideShortcutCaptureRef.current;
      if (cap) {
        if (!activeSlide?.id) return;
        if (String(activeSlide.id) !== String(cap.presentationId)) return;
        if (shouldDeferDeckKeys(e.target)) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          setSlideShortcutCapture(null);
          return;
        }
        if (e.code === 'Backspace' || e.code === 'Delete') {
          if (!isSlideShortcutBindingAllowed(e, { allowBackspaceDelete: true })) return;
          e.preventDefault();
          const token = userRef.current?.access_token;
          const pack = selectedPackRef.current;
          if (!token || !pack?.id) {
            setSlideShortcutCapture(null);
            return;
          }
          const nextDs = applyDeckSlideShortcutBinding(pack.deckSlideShortcuts, cap.presentationId, cap.objectId, null);
          const nextPack = { ...pack, deckSlideShortcuts: nextDs };
          setSelectedPack(nextPack);
          setSlideShortcutCapture(null);
          void (async () => {
            try {
              await savePack(token, pack.id, packPersistencePayload(nextPack));
            } catch (err) {
              console.error('Persist slide shortcut failed', err);
            }
          })();
          return;
        }
        if (!isSlideShortcutBindingAllowed(e)) {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        const token = userRef.current?.access_token;
        const pack = selectedPackRef.current;
        if (!token || !pack?.id) {
          setSlideShortcutCapture(null);
          return;
        }
        const nextDs = applyDeckSlideShortcutBinding(pack.deckSlideShortcuts, cap.presentationId, cap.objectId, e.code);
        const nextPack = { ...pack, deckSlideShortcuts: nextDs };
        setSelectedPack(nextPack);
        setSlideShortcutCapture(null);
        void (async () => {
          try {
            await savePack(token, pack.id, packPersistencePayload(nextPack));
          } catch (err) {
            console.error('Persist slide shortcut failed', err);
          }
        })();
        return;
      }

      // Do not require Slides API metadata here: slide index still advances while thumbnails load.
      if (!activeSlide?.id) return;
      if (shouldDeferDeckKeys(e.target)) return;

      if (
        slideJumpCodeToObjectId.size > 0 &&
        !e.repeat &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey
      ) {
        const oid = slideJumpCodeToObjectId.get(e.code);
        if (oid) {
          const slides = slideContentRef.current[activeSlide.id]?.slides;
          const idx = Array.isArray(slides)
            ? slides.findIndex((s) => String(s.objectId) === String(oid))
            : -1;
          if (idx >= 0) {
            e.preventDefault();
            handleSlideChange(idx);
            return;
          }
        }
      }

      if (e.key === '.' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        bumpInSlideRevealAllWindowsRef.current();
        remoteGoNextSlideRef.current();
        return;
      }

      const deckForward =
        e.code === 'Space' ||
        e.key === ' ' ||
        e.code === 'PageDown' ||
        e.code === 'ArrowRight' ||
        e.code === 'ArrowDown' ||
        e.key === 'ArrowRight' ||
        e.key === 'ArrowDown';

      if (deckForward) {
        e.preventDefault();
        bumpInSlideRevealAllWindowsRef.current();
        remoteGoNextSlideRef.current();
        return;
      }

      if (
        e.code === 'ArrowLeft' ||
        e.code === 'ArrowUp' ||
        e.code === 'PageUp' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowUp' ||
        e.key === 'PageUp'
      ) {
        e.preventDefault();
        remoteGoPrevSlideRef.current();
      }
    };

    window.addEventListener('keydown', handleKeyPress, true);
    return () => window.removeEventListener('keydown', handleKeyPress, true);
  }, [activeSlide, handleSlideChange, slideJumpCodeToObjectId]);

  // Handle drag-to-resize between preview and notes
  useEffect(() => {
    if (!isResizingPreview) return;

    const handleMouseMove = (e) => {
      if (!previewNotesRef.current) return;
      const rect = previewNotesRef.current.getBoundingClientRect();
      const offsetY = e.clientY - rect.top;
      const ratio = offsetY / rect.height;
      // Clamp between 0.4 and 0.85 to keep slideshow usable
      const clamped = Math.min(0.85, Math.max(0.4, ratio));
      setPreviewHeightRatio(clamped);
    };

    const handleMouseUp = () => {
      setIsResizingPreview(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingPreview]);

  const handleLogin = () => {
    beginGoogleOAuthCodeFlow().catch((e) => console.error('OAuth start failed', e));
  };

  const handleLogout = () => {
    // Standard logout actions
    setUser(null);
    setIsAuthenticated(false);
    setShowDropdown(false);
    setSelectedPack(null);
    setActiveSlide(null);
    
    // Clear auth
    localStorage.removeItem("user_auth");
    
    // Signal child windows to close
    localStorage.setItem('app-session-state', JSON.stringify({
      action: 'logout',
      timestamp: Date.now()
    }));
  };

  const handleCreatePackFromModal = async () => {
    const packName = await appPrompt({
      title: 'Create new Pack',
      message: 'Enter a name for the new folder in Google Drive.',
      placeholder: 'Pack name',
      confirmLabel: 'Create',
      defaultValue: '',
    });
    if (packName == null) return;
    const trimmed = packName.trim();
    if (!trimmed || !user?.access_token) return;
    const nameExists = packs.some((p) => p.name.toLowerCase() === trimmed.toLowerCase());
      if (nameExists) {
      await appAlert({
        title: 'Name in use',
        message: 'A pack with this name already exists. Please choose a different name.',
      });
        return;
      }
    if (!getGooglePickerDeveloperKey()) {
      await appAlert({
        title: 'Google Picker API key',
        message:
          'Add REACT_APP_GOOGLE_PICKER_API_KEY to your environment (browser API key with Picker API enabled), then restart the dev server.',
      });
      return;
    }
    flushSync(() => setIsPacksActionModalOpen(false));
    await deferPickerOpen();
    try {
      const parentFolder = await openGoogleDriveFolderPicker({
        accessToken: user.access_token,
        title: 'Choose where to create the pack folder',
      });
      let parentId = parentFolder?.id;
      if (!parentId) {
        const useRoot = await appConfirm({
          title: 'Create pack at My Drive root?',
          message:
            'No parent folder was selected in the picker. Create this pack as a new folder at the top of My Drive (root) instead?',
          confirmLabel: 'Create at root',
          cancelLabel: 'Cancel',
        });
        if (!useRoot) {
          setIsPacksActionModalOpen(true);
          return;
        }
        parentId = undefined;
      }
      const newPack = await createPackFile(user.access_token, trimmed, parentId);
      if (newPack) {
        const updatedPacks = await listPackFiles(user.access_token);
        setPacks(sortPacksByName(updatedPacks));
        await handleSelectPack(String(newPack.id), { displayName: newPack.name });
      }
    } catch (e) {
      console.error('Create pack / folder picker failed:', e);
      await appAlert({
        title: 'Could not create pack',
        message: e?.message || String(e),
      });
      setIsPacksActionModalOpen(true);
    }
  };

  const handleUseFolderAsPack = async (folder) => {
    if (!user?.access_token || !folder?.id) return;
    try {
      const folderId = String(folder.id);
      const displayName = folder.name || '';
      // Ensure pack.dpack exists in this folder
      const packInfo = await ensurePackForFolder(user.access_token, folderId, displayName);
        const updatedPacks = await listPackFiles(user.access_token);
        setPacks(sortPacksByName(updatedPacks));
      await handleSelectPack(String(packInfo.id), {
        displayName: packInfo.name || displayName,
      });
    } catch (err) {
      console.error('Failed to use folder as pack:', err);
      await appAlert({
        title: "Couldn't use that folder",
        message: err?.message || 'Please try another folder or check Drive permissions.',
      });
    }
  };

  const handlePackRename = async (packId) => {
    const currentName = packs.find((p) => p.id === packId)?.name || 'this pack';
    const newName = await appPrompt({
      title: 'Rename pack',
      message: `This renames the Google Drive folder for "${currentName}" — the new name appears in Drive and in Deck Pack.`,
      defaultValue: currentName,
      placeholder: 'New pack name',
      confirmLabel: 'Rename',
    });
    if (newName == null) return;
    const trimmed = newName.trim();
    if (!trimmed || !user?.access_token) return;
    const success = await updatePackName(user.access_token, packId, trimmed);
      if (success) {
        const updatedPacks = await listPackFiles(user.access_token);
        setPacks(sortPacksByName(updatedPacks));
      setSelectedPack((prev) => (prev && prev.id === packId ? { ...prev, name: trimmed } : prev));
    } else {
      await appAlert({
        title: 'Rename failed',
        message:
          'Google Drive could not rename this pack folder. If it lives on a Shared drive, confirm you have edit access, then try again.',
      });
    }
  };

  const handleDragStart = ({ active }) => {
    const activeItem = document.querySelector(`[data-id="${active.id}"]`);
    if (activeItem) activeItem.classList.add('dragging');
  };

  const handleDragOver = ({ active, over }) => {
    if (!over) return;
    
    const activeId = active.id;
    const overId = over.id;
    
    if (activeId === overId) return;

    const slides = selectedPackRef.current?.slides ?? [];
    const activeIndex = slides.findIndex((s, i) => playlistSortableId(s, i) === String(activeId));
    const overIndex = slides.findIndex((s, i) => playlistSortableId(s, i) === String(overId));
    if (activeIndex < 0 || overIndex < 0) return;
    
    const container = document.querySelector('#playlist-section .library-items');
    const overItem = container.querySelector(`[data-id="${overId}"]`);
    const indicator = container.querySelector('.drag-indicator');
    
    if (overItem && indicator) {
      const rect = overItem.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      
      const y = (activeIndex < overIndex ? rect.bottom : rect.top) 
        - containerRect.top 
        + container.scrollTop;
      
      indicator.style.transform = `translateY(${y}px)`;
      indicator.style.opacity = '1';
    }
  };

  const handleDragEnd = async (event) => {
    const indicator = document.querySelector('.drag-indicator');
    if (indicator) {
      indicator.style.opacity = '0';
      indicator.style.transform = 'none';
    }

    const activeEl = document.querySelector(`[data-id="${event.active?.id}"]`);
    if (activeEl) activeEl.classList.remove('dragging');

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const slidesSnap = selectedPackRef.current?.slides ?? selectedPack.slides ?? [];
    const oldIndex = slidesSnap.findIndex((s, i) => playlistSortableId(s, i) === String(active.id));
    const newIndex = slidesSnap.findIndex((s, i) => playlistSortableId(s, i) === String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;

    const newSlides = [...slidesSnap];
    const [movedSlide] = newSlides.splice(oldIndex, 1);
    newSlides.splice(newIndex, 0, movedSlide);

    const packSnap = selectedPackRef.current;
    if (packSnap?.id) {
      setSelectedPack({ ...packSnap, slides: newSlides });
      void savePack(
        user.access_token,
        packSnap.id,
        packPersistencePayload(packSnap, { slides: newSlides })
      );
    }
  };
  
  /** Add a deck to the current pack playlist. `fromPicker`: try moving into the pack folder, then add (no extra dialogs). */
  const addDeckToPlaylist = async (deck, opts = {}) => {
    const { fromPicker = false } = opts;
    if (!deck?.id) {
      setAddFeedback({
        show: true,
        message: 'That file had no usable ID from Google — try picking the deck again.',
        type: 'error',
        deckId: null,
      });
      setTimeout(() => setAddFeedback({ show: false, message: '', type: '', deckId: null, deckToAdd: null }), 4000);
      return false;
    }
    const deckIdStr = String(deck.id);

    let pack = selectedPackRef.current;
    if (!pack?.id || !user?.access_token) {
        setAddFeedback({
          show: true,
        message: !user?.access_token
          ? 'Not signed in — refresh and sign in again.'
          : 'No pack is selected. Click a pack in the sidebar first.',
        type: 'error',
        deckId: deckIdStr,
      });
      setTimeout(() => setAddFeedback({ show: false, message: '', type: '', deckId: null, deckToAdd: null }), 4000);
      return false;
    }

    if (isPackLoading) {
      setAddFeedback({
        show: true,
        message: 'This pack is still loading from Drive. Wait a moment, then add again.',
        type: 'info',
        deckId: deckIdStr,
      });
      setTimeout(() => setAddFeedback({ show: false, message: '', type: '', deckId: null, deckToAdd: null }), 2500);
      return false;
    }

    const slideIds = new Set((pack.slides || []).map((s) => String(s.id)));
    if (slideIds.has(deckIdStr)) {
      setAddFeedback({
        show: true,
        message: 'Already in playlist',
        type: 'info',
        deckId: deckIdStr,
      });
      setTimeout(() => setAddFeedback({ show: false, message: '', type: '', deckId: null, deckToAdd: null }), 2000);
      return false;
    }

    const packName = pack.name || 'this pack';
    const alreadyInPackFolder = await fileIsInFolder(user.access_token, deckIdStr, pack.id);
    pack = selectedPackRef.current;
    if (!pack?.id) {
      setAddFeedback({
        show: true,
        message: 'The active pack changed while adding — click the pack again, then retry.',
        type: 'info',
        deckId: deckIdStr,
      });
      setTimeout(() => setAddFeedback({ show: false, message: '', type: '', deckId: null, deckToAdd: null }), 4000);
      return false;
    }

    const isSharedNotOwned = deck.ownedByMe === false;

    let movedIntoFolder = false;
    let source = deck.source || 'external';
    let moveIntoPackAttempted = false;
    let addLinkOnlyChosen = false;

    if (isSharedNotOwned) {
      if (!fromPicker) {
        const ok = await appConfirm({
          title: 'Add shared deck?',
          message:
            `"${deck.name}" is shared with you. It stays in its current location in Google Drive — Deck Pack will only add it to this playlist (no file move).\n\nContinue?`,
          confirmLabel: 'Continue',
        });
        if (!ok) return false;
      }
      movedIntoFolder = false;
      source = 'shared';
    } else if (!alreadyInPackFolder) {
      pack = selectedPackRef.current;
      if (!pack?.id) {
        setAddFeedback({
          show: true,
          message: 'Pack changed while preparing the add — select the pack again and retry.',
          type: 'info',
          deckId: deckIdStr,
        });
        setTimeout(() => setAddFeedback({ show: false, message: '', type: '', deckId: null, deckToAdd: null }), 4000);
        return false;
      }
      if (fromPicker) {
        moveIntoPackAttempted = true;
        movedIntoFolder = await moveFileIntoFolder(user.access_token, deckIdStr, pack.id);
        source = movedIntoFolder ? 'folder' : 'external';
        if (!movedIntoFolder) addLinkOnlyChosen = true;
      } else {
        const moveIntoPack = await appConfirm({
          title: 'Add deck to playlist',
          message:
            `"${deck.name}" is not inside the "${packName}" folder yet.\n\n` +
            `• Move into pack folder: put the Slides file in this pack’s Drive folder (recommended).\n` +
            `• Add link only: keep the file where it is and only add a playlist entry.`,
          confirmLabel: 'Move into pack folder',
          cancelLabel: 'Add link only',
        });
        pack = selectedPackRef.current;
        if (!pack?.id) {
          setAddFeedback({
            show: true,
            message: 'Pack changed — select the pack again and retry.',
            type: 'info',
            deckId: deckIdStr,
          });
          setTimeout(() => setAddFeedback({ show: false, message: '', type: '', deckId: null, deckToAdd: null }), 4000);
          return false;
        }
        if (moveIntoPack) {
          moveIntoPackAttempted = true;
          movedIntoFolder = await moveFileIntoFolder(user.access_token, deckIdStr, pack.id);
          source = movedIntoFolder ? 'folder' : 'external';
        } else {
          addLinkOnlyChosen = true;
          movedIntoFolder = false;
          source = 'external';
        }
      }
    } else {
      movedIntoFolder = true;
      source = 'folder';
    }

    pack = selectedPackRef.current;
    if (!pack?.id) {
      setAddFeedback({
        show: true,
        message: 'Pack is no longer selected — choose a pack, then add the deck again.',
        type: 'info',
        deckId: deckIdStr,
      });
      setTimeout(() => setAddFeedback({ show: false, message: '', type: '', deckId: null, deckToAdd: null }), 4000);
      return false;
    }

    const targetPackIdStr = String(pack.id);
    const wasEmpty = !(pack.slides || []).length;
    const deckWithUniqueId = {
      ...deck,
      id: deckIdStr,
      playlistPositionId: Date.now(),
      source,
    };

    // Must run the updater synchronously: after `await`, a plain setState updater can run
    // after this function returns, so `nextPayload` was never set and we skipped savePack —
    // then sync reloads manifest and the deck never appears.
    let nextPayload = null;
    try {
      flushSync(() => {
        setSelectedPack((prev) => {
          if (!prev || String(prev.id) !== targetPackIdStr) return prev;
          if ((prev.slides || []).some((s) => String(s.id) === deckIdStr)) return prev;
          const newSlides = [...(prev.slides || []), deckWithUniqueId];
          const newRemovedFileIds = (prev.removedFileIds || []).filter((id) => String(id) !== deckIdStr);
          nextPayload = {
            slides: newSlides,
            removedFileIds: newRemovedFileIds,
            deckSlideShortcuts: prev.deckSlideShortcuts ?? {},
          };
          return { ...prev, slides: newSlides, removedFileIds: newRemovedFileIds };
        });
      });
    } catch (e) {
      console.error('flushSync while adding deck:', e);
    }

    if (!nextPayload) {
      const snap = selectedPackRef.current;
      if (
        snap?.id &&
        String(snap.id) === targetPackIdStr &&
        !(snap.slides || []).some((s) => String(s.id) === deckIdStr)
      ) {
        const newSlides = [...(snap.slides || []), deckWithUniqueId];
        const newRemovedFileIds = (snap.removedFileIds || []).filter((id) => String(id) !== deckIdStr);
        nextPayload = {
          slides: newSlides,
          removedFileIds: newRemovedFileIds,
          deckSlideShortcuts: snap.deckSlideShortcuts ?? {},
        };
        setSelectedPack({ ...snap, slides: newSlides, removedFileIds: newRemovedFileIds });
      }
    }

    if (!nextPayload) {
    setAddFeedback({
      show: true,
        message: `Could not merge deck into the playlist UI (pack id mismatch or duplicate). Pack: ${targetPackIdStr}, deck: ${deckIdStr}. Try reloading the page.`,
        type: 'error',
        deckId: deckIdStr,
      });
      setTimeout(() => setAddFeedback({ show: false, message: '', type: '', deckId: null, deckToAdd: null }), 8000);
      return false;
    }

    if (wasEmpty) {
      setActiveSlide(deckWithUniqueId);
      setCurrentSlideIndex(0);
      if (deckWithUniqueId.id) {
        persistCurrentSlideState(deckWithUniqueId.id, 0, slideContent);
        localStorage.setItem(
          'active-presentation',
          JSON.stringify({
            presentationId: deckWithUniqueId.id,
            playlistPositionId: deckWithUniqueId.playlistPositionId,
            name: deckWithUniqueId.name,
            timestamp: Date.now(),
          })
        );
      }
    }
    setLastAddedDeckId(deckIdStr);
    const addMessage = isSharedNotOwned
      ? 'Added to playlist (shared deck — left in original Drive location)'
      : moveIntoPackAttempted && !movedIntoFolder
        ? 'Added to playlist (could not move file into pack folder)'
        : addLinkOnlyChosen
          ? 'Added to playlist (link only — file left in place)'
          : 'Added to playlist';
    const addType = addMessage === 'Added to playlist' ? 'success' : 'info';

    setAddFeedback({
      show: true,
      message: addMessage,
      type: addType,
      deckId: deckIdStr,
    });

    let savedOk = false;
    try {
      const saveResult = await savePackWithMeta(user.access_token, targetPackIdStr, nextPayload);
      if (!saveResult.ok) {
        setSelectedPack((prev) => {
          if (!prev || String(prev.id) !== targetPackIdStr) return prev;
          return {
            ...prev,
            slides: (prev.slides || []).filter(
              (s) => s.playlistPositionId !== deckWithUniqueId.playlistPositionId
            ),
            removedFileIds: prev.removedFileIds || [],
          };
        });
        const hint =
          saveResult.step === 'no-dpack'
            ? 'This folder has no pack.dpack manifest yet. Try re-opening the pack or “Remove as Pack” then add the folder again.'
            : saveResult.status === 403 || saveResult.status === 401
              ? 'Google returned forbidden/unauthorized — sign out and back in, or check Shared drive edit access.'
              : 'Could not save playlist to Drive.';
        const detail = saveResult.message
          ? `${hint} (${saveResult.status ?? saveResult.step ?? 'error'}: ${saveResult.message.slice(0, 240)})`
          : hint;
        setAddFeedback({
          show: true,
          message: detail,
          type: 'error',
          deckId: deckIdStr,
        });
      } else {
        packLoadGenerationRef.current += 1;
        savedOk = true;
      }
    } catch (err) {
      console.error('Failed to save playlist', err);
      setAddFeedback({ show: true, message: 'Failed to save. Try again.', type: 'error', deckId: deckIdStr });
    }

    setTimeout(() => setAddFeedback({ show: false, message: '', type: '', deckId: null, deckToAdd: null }), 2000);
    setTimeout(() => setLastAddedDeckId(null), 2000);
    return savedOk;
  };

  const handlePickFolderForPack = async () => {
    if (!user?.access_token) return;
    if (!getGooglePickerDeveloperKey()) {
      await appAlert({
        title: 'Google Picker API key',
        message:
          'Add REACT_APP_GOOGLE_PICKER_API_KEY to your environment (browser API key with Picker API enabled), then restart the dev server.',
      });
      return;
    }
    flushSync(() => setIsPacksActionModalOpen(false));
    await deferPickerOpen();
    try {
      const folder = await openGoogleDriveFolderPicker({
        accessToken: user.access_token,
        title: 'Choose a Google Drive folder as a Pack',
      });
      if (!folder?.id) {
        setIsPacksActionModalOpen(true);
        return;
      }
      await handleUseFolderAsPack(folder);
    } catch (e) {
      console.error(e);
      await appAlert({
        title: 'Folder picker',
        message: e?.message || String(e),
      });
      setIsPacksActionModalOpen(true);
    }
  };

  const handlePickPresentationsForPlaylist = async () => {
    if (!user?.access_token || !selectedPack) {
      await appAlert({
        title: 'Select a Pack',
        message: 'Open or create a Pack first, then add presentations from Google Drive.',
      });
      return;
    }
    if (isPackLoading) {
      await appAlert({
        title: 'Pack is still loading',
        message: 'Wait until this pack finishes loading from Google Drive, then click + again.',
      });
      return;
    }
    if (!getGooglePickerDeveloperKey()) {
      await appAlert({
        title: 'Google Picker API key',
        message:
          'Add REACT_APP_GOOGLE_PICKER_API_KEY to your environment (browser API key with Picker API enabled), then restart the dev server.',
      });
      return;
    }
    await deferPickerOpen();
    try {
      const docs = await openGoogleSlidesPresentationPicker({
        accessToken: user.access_token,
        title: 'Add presentations to playlist',
        multiselect: true,
      });
      if (!docs.length) {
        setAddFeedback({
          show: true,
          message: 'No files were selected in the picker (or it was cancelled).',
          type: 'info',
          deckId: null,
        });
        setTimeout(() => setAddFeedback({ show: false, message: '', type: '', deckId: null, deckToAdd: null }), 3500);
        return;
      }
      let addedCount = 0;
      for (const doc of docs) {
        if (!doc?.id) continue;
        const idStr = String(doc.id);
        const meta = await getDriveFileBasic(user.access_token, idStr);
        const deck = {
          id: idStr,
          name: meta?.name || doc.name || 'Untitled',
          ownedByMe: meta?.ownedByMe !== false,
        };
        const ok = await addDeckToPlaylist(deck, { fromPicker: true });
        if (ok) addedCount += 1;
      }
      if (addedCount === 0 && docs.length > 0) {
        setAddFeedback({
          show: true,
          message:
            'Picker returned files but none were added. Check the message above the playlist, or reload and try again.',
          type: 'error',
          deckId: null,
        });
        setTimeout(() => setAddFeedback({ show: false, message: '', type: '', deckId: null, deckToAdd: null }), 6000);
      }
    } catch (e) {
      console.error(e);
      await appAlert({
        title: 'Presentation picker',
        message: e?.message || String(e),
      });
    }
  };

  const SortableItem = ({
    id,
    index,
    slide,
    isActive,
    isJustAdded,
    onSelect,
    onDelete,
    onDuplicate,
    folderBackedOriginal,
  }) => {
    const { 
      attributes, 
      listeners, 
      setNodeRef, 
      transform, 
      transition,
      isDragging
    } = useSortable({ id });

    const style = {
      transform: transform ? CSS.Transform.toString(transform) : undefined,
      transition
    };

    const isDuplicate = !!slide.duplicateOf;

    return (
      <div
        ref={setNodeRef}
        data-id={id}
        style={style}
        className={`library-row${isActive ? ' library-row--selected' : ''}${isDragging ? ' library-row--dragging' : ''}${isJustAdded ? ' library-row--just-added' : ''}`}
      >
        <div
          className="library-item library-item--content"
        onClick={onSelect}
        {...attributes}
        {...listeners}
      >
          <div className="library-item-body">
        <span className="item-number">{index + 1}.</span>
            <span className="item-name">
              {slide.name}
              {isDuplicate && (
                <span className="duplicate-badge">Copy</span>
              )}
            </span>
          </div>
        </div>
        <div
          className="library-actions-section library-actions-section--two-slot"
          role="group"
          aria-label="Playlist deck actions"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="library-actions-slot">
            {onDuplicate ? (
          <button 
                type="button"
                className="row-icon-button"
                title="Duplicate in playlist"
                aria-label="Duplicate in playlist"
            onClick={(e) => {
              e.stopPropagation();
                  onDuplicate();
            }}
          >
                <span className="row-icon-button__glyph" aria-hidden="true">
                  ⧉
                </span>
          </button>
            ) : null}
          </div>
          <div className="library-actions-slot">
            <button
              type="button"
              className="row-icon-button row-icon-button--danger"
              title={
                folderBackedOriginal
                  ? 'Remove from playlist — moves the file in Google Drive (My Drive root or folder you pick)'
                  : isDuplicate
                    ? 'Remove copy from playlist'
                    : 'Remove from playlist'
              }
              aria-label={
                folderBackedOriginal
                  ? 'Remove from playlist; moves file in Google Drive'
                  : isDuplicate
                    ? 'Remove copy from playlist'
                    : 'Remove from playlist'
              }
              onClick={(e) => {
                e.stopPropagation();
                void onDelete();
              }}
            >
              <i className="fa-solid fa-trash-can" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPlaylist = () => {
    return (selectedPack?.slides || []).map((slide, index) => (
      <SortableItem 
        key={playlistSortableId(slide, index)}
        id={playlistSortableId(slide, index)}
        index={index}
        slide={slide}
        isActive={activeSlide?.playlistPositionId === slide.playlistPositionId}
        isJustAdded={slide.id === lastAddedDeckId}
        onSelect={() => {
          setActiveSlide(slide);
          setCurrentSlideIndex(0);
          
          // Only update localStorage if slide is valid
          if (slide && slide.id) {
            persistCurrentSlideState(slide.id, 0, slideContent);
            
            // Store active presentation for presenter view
            localStorage.setItem('active-presentation', JSON.stringify({
              presentationId: slide.id,
              playlistPositionId: slide.playlistPositionId,
              name: slide.name,
              timestamp: Date.now()
            }));
          }
        }}
        folderBackedOriginal={slide.source === 'folder' && !slide.duplicateOf}
        onDelete={async () => {
          // For original folder items, offer to move the file out.
          // For duplicates and non-folder items, just unlink from the playlist.
          if (slide.source === 'folder' && !slide.duplicateOf) {
            const destinationChoice = await appConfirm({
              title: 'Remove from playlist',
              message:
                `"${slide.name || 'This deck'}" is stored inside this pack's Google Drive folder.\n\n` +
                'Move the file to the top of your My Drive (fast), or choose another Drive folder in the Google picker.',
              confirmLabel: 'Move to My Drive root',
              cancelLabel: 'Choose another folder…',
              danger: true,
            });
            if (destinationChoice !== true && destinationChoice !== false) return;

            let destId = null;
            if (destinationChoice === true) {
              destId = 'root';
            } else if (!getGooglePickerDeveloperKey()) {
              const fallbackRoot = await appConfirm({
                title: 'Google Picker API key',
                message:
                  'Choosing another folder needs REACT_APP_GOOGLE_PICKER_API_KEY in your environment. Move this deck to My Drive root instead (no picker)?',
                confirmLabel: 'Move to My Drive root',
                cancelLabel: 'Cancel',
                danger: true,
              });
              if (!fallbackRoot) return;
              destId = 'root';
            } else {
              await deferPickerOpen();
              const destFolder = await openGoogleDriveFolderPicker({
                accessToken: user.access_token,
                title: 'Move deck to folder',
              });
              destId = destFolder?.id;
              if (!destId) {
                const useRoot = await appConfirm({
                  title: 'Move to My Drive root?',
                  message:
                    'No folder was selected. Move this deck to the top of your My Drive (root) and remove it from this playlist?',
                  confirmLabel: 'Move to My Drive root',
                  cancelLabel: 'Cancel',
                  danger: true,
                });
                if (!useRoot) return;
                destId = 'root';
              }
            }

            try {
          const newSlides = selectedPack.slides.filter((_, i) => i !== index);
              const newRemovedFileIds = [...(selectedPack.removedFileIds || []), slide.id];
              const packAfterRemove = { ...selectedPack, slides: newSlides, removedFileIds: newRemovedFileIds };
              setSelectedPack(packAfterRemove);
              if (activeSlide?.playlistPositionId === slide.playlistPositionId) {
                setActiveSlide(newSlides[0] || null);
                setCurrentSlideIndex(0);
              }
              // Persist removal first so background sync cannot re-add the item
              await savePack(user.access_token, selectedPack.id, packPersistencePayload(packAfterRemove));
              const ok = await moveFileToFolder(user.access_token, slide.id, selectedPack.id, destId);
              if (ok) {
                const result = await syncPackWithFolder(user.access_token, selectedPack.id);
                const synced = result?.slides ?? result ?? [];
                if (Array.isArray(synced) && synced.length !== undefined) {
                  const withIds = synced.map((s, i) =>
                    s.playlistPositionId ? s : { ...s, playlistPositionId: Date.now() + i }
                  );
                  const mergedRemoved = result?.removedFileIds ?? newRemovedFileIds;
                  const packAfterSync = {
                    ...selectedPack,
                    slides: withIds,
                    removedFileIds: mergedRemoved,
                    deckSlideShortcuts: mergeDeckSlideShortcutsPreferLocal(
                      packAfterRemove.deckSlideShortcuts,
                      result?.deckSlideShortcuts ?? {}
                    ),
                  };
                  setSelectedPack(packAfterSync);
                  await savePack(user.access_token, selectedPack.id, packPersistencePayload(packAfterSync));
                }
              } else {
                console.warn('Could not move file to My Drive; removed from playlist only.');
              }
            } catch (err) {
              console.error('Error removing playlist item:', err);
              await appAlert({ title: 'Remove failed', message: 'Failed to remove item. Please try again.' });
              setSelectedPack({ ...selectedPack });
            }
          } else {
            if (slide.source === 'shared') {
              const sharedMsg = slide.duplicateOf
                ? 'Remove this duplicate entry from the playlist? The shared file in Google Drive is unchanged.'
                : 'Remove this shared presentation from the playlist? The file stays in its original location in Google Drive; only this playlist in Deck Pack changes.';
              const sharedOk = await appConfirm({
                title: 'Remove from playlist',
                message: sharedMsg,
                confirmLabel: 'Remove',
                danger: true,
              });
              if (!sharedOk) return;
            } else {
              const genericOk = await appConfirm({
                title: 'Remove from playlist?',
                message:
                  slide.duplicateOf && slide.source === 'folder'
                    ? 'Remove this duplicate entry from the playlist? The file in the pack folder is unchanged.'
                    : 'Remove this deck from the playlist? The file in Google Drive is unchanged.',
                confirmLabel: 'Remove',
                danger: true,
              });
              if (!genericOk) return;
            }

            let moveLinkOnlyToMyDriveRoot = false;
            if (
              !slide.duplicateOf &&
              slide.source !== 'shared' &&
              slide.source !== 'folder' &&
              slide.ownedByMe !== false
            ) {
              const moveAsk = await appConfirm({
                title: 'Also move in Google Drive?',
                message:
                  `This deck is linked in the playlist but its file is not kept inside the pack folder.\n\n` +
                  `After it is removed from this playlist, move the Slides file to the top of your My Drive (root) so it is easy to find?`,
                confirmLabel: 'Move to My Drive root',
                cancelLabel: 'Leave file where it is',
              });
              if (moveAsk) moveLinkOnlyToMyDriveRoot = true;
            }

            try {
              const newSlides = selectedPack.slides.filter((_, i) => i !== index);
              const packAfterRemove = { ...selectedPack, slides: newSlides };
              setSelectedPack(packAfterRemove);
              if (activeSlide?.playlistPositionId === slide.playlistPositionId) {
                setActiveSlide(newSlides[0] || null);
                setCurrentSlideIndex(0);
              }
              await savePack(user.access_token, selectedPack.id, packPersistencePayload(packAfterRemove));
              if (moveLinkOnlyToMyDriveRoot) {
                const moved = await moveFileIntoFolder(user.access_token, slide.id, 'root');
                if (!moved) {
                  await appAlert({
                    title: 'Playlist updated',
                    message:
                      'The deck was removed from this playlist, but Google Drive could not move the file to My Drive root. You can move it manually in drive.google.com if needed.',
                  });
                }
              }
            } catch (err) {
              console.error('Error removing playlist item:', err);
              await appAlert({ title: 'Remove failed', message: 'Failed to remove item. Please try again.' });
            }
          }
        }}
        onDuplicate={() => {
          const pack = selectedPackRef.current;
          if (!pack?.slides?.length || !user?.access_token || !pack.id) return;
          const newSlides = [...pack.slides];
          const row = newSlides[index];
          if (!row) return;
          const rootDuplicateOf = row.duplicateOf != null ? row.duplicateOf : row.playlistPositionId;
          const duplicate = {
            ...row,
            playlistPositionId: Date.now(),
            duplicateOf: rootDuplicateOf,
          };
          newSlides.splice(index + 1, 0, duplicate);
          const nextPack = { ...pack, slides: newSlides };
          setSelectedPack(nextPack);
          void savePack(user.access_token, pack.id, packPersistencePayload(nextPack));
        }}
      />
    ));
  };

  return (
    <div className="app">
      {isScreenTooSmall ? (
        <div className="screen-too-small">
          <div className="screen-too-small-content">
            <img src={logo} alt="Deck Pack Logo" className="screen-too-small-logo" />
          <h1>Deck Pack</h1>
            <p>Please move to a full-sized screen to use Deck Pack.</p>
          </div>
        </div>
      ) : !isAuthenticated ? (
        <div className="login-container">
          <div className="login-bg" aria-hidden="true">
            <div className="login-bg-shape login-bg-shape--tl" />
            <div className="login-bg-shape login-bg-shape--bl" />
            <div className="login-bg-shape login-bg-shape--tr" />
          </div>

          <div className="login-hero-wrap">
            <div className="login-hero">
              <header className="login-header">
                <div className="login-brand">
                  <img src={logo} alt="Deck Pack Logo" className="app-logo login-hero-logo" />
                </div>
                <h1 className="login-title">Deck Pack</h1>
              </header>

              <p className="login-intro-lead">
                Line up the Google Slides you already keep in Drive—like a set list for the stage or the
                classroom. One place to order decks, see what&apos;s next, and keep your head in the room
                instead of{' '}
                <span className="login-intro-nobreak">the file picker.</span>
              </p>

              <div className="login-features" role="list">
                <div className="login-feature" role="listitem">
                  <i
                    className="fa-solid fa-list-ol login-feature-icon login-feature-icon--playlist"
                    aria-hidden="true"
                  />
                  <span className="login-feature-label">Deck Playlists</span>
                </div>
                <div className="login-feature" role="listitem">
                  <i
                    className="fa-solid fa-chalkboard-user login-feature-icon login-feature-icon--presenter"
                    aria-hidden="true"
                  />
                  <span className="login-feature-label">Presenter View</span>
                </div>
                <div className="login-feature" role="listitem">
                  <i
                    className="fa-solid fa-tv login-feature-icon login-feature-icon--audience"
                    aria-hidden="true"
                  />
                  <span className="login-feature-label">Audience View</span>
                </div>
              </div>

              <div className="login-cta">
                <p className="login-cta-label">Start with your Google account</p>
          <GoogleButton onClick={handleLogin} />
              </div>
            </div>
          </div>

          <footer className="login-footer">
            <p className="login-footer-text">
              Sign in with Google allows Deck Pack access only to the folders and decks you choose. We
              don&apos;t sell your data or sell it to advertisers. Read our{' '}
              <a
                className="login-footer-policy-link"
                href={`${process.env.PUBLIC_URL || ''}/privacy.html`}
                target="_blank"
                rel="noopener noreferrer"
              >
                privacy policy
              </a>
              .
            </p>
          </footer>
        </div>
      ) : (
        <div className="propresenter-layout">
          <div className="sidebar">
            <div className="sidebar-header">
              <div className="app-title">
              <img src={logo} alt="Deck Pack Logo" className="app-logo" />
                <h1>Deck Pack</h1>
              </div>
              <div className="sidebar-header-trailing">
                <button
                  type="button"
                  className="tour-help-button"
                  title="App tour"
                  aria-label="Start app tour"
                  onClick={() => setTourOpen(true)}
                >
                  <i className="fa-regular fa-circle-question" aria-hidden="true" />
                </button>
              <div className="profile-menu">
                {!profileImageError && user?.imageUrl ? (
                <img 
                  src={user.imageUrl} 
                    alt={user.name || 'Profile'} 
                  className="profile-photo"
                    referrerPolicy="no-referrer"
                    onError={() => setProfileImageError(true)}
                  onClick={() => setShowDropdown(!showDropdown)} 
                />
                ) : (
                  <div 
                    className="profile-fallback profile-fallback-icon" 
                    onClick={() => setShowDropdown(!showDropdown)}
                    title={user?.name || 'Profile'}
                  >
                    <i className="fa-regular fa-user" aria-hidden="true" />
                  </div>
                )}
                {showDropdown && (
                  <div className="dropdown-menu">
                    <button onClick={handleLogout}>Sign Out</button>
                  </div>
                )}
              </div>
              </div>
            </div>
            <div className="sidebar-content">
              {tourLayoutPlaceholders ? (
                <TourPlaceholderSidebar
                  onOpenPacksModal={() => setIsPacksActionModalOpen(true)}
                  autoAdvance={autoAdvance}
                  setAutoAdvance={setAutoAdvance}
                />
              ) : (
                <>
              {/* Packs section - only shown when a pack is selected */}
              {selectedPack && (
                <div className="library-section" data-tour="tour-packs">
                  <div className="section-header">
                    <h2>Packs</h2>
                    <button 
                      className="add-pack-button"
                      onClick={() => setIsPacksActionModalOpen(true)}
                      title="Create or select pack"
                    >
                      <i className="fa-solid fa-plus" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="library-items">
                    {packs.map((pack) => (
                      <div
                        key={pack.id}
                        className={`library-row${selectedPack?.id === pack.id ? ' library-row--selected' : ''}`}
                      >
                        <div
                          className="library-item library-item--content"
                          onClick={() => handleSelectPack(pack.id)}
                        >
                          <div className="library-item-body">
                            <span className="item-name item-name--pack">{pack.name}</span>
                          </div>
                        </div>
                        <div
                          className="library-actions-section library-actions-section--two-slot"
                          role="group"
                          aria-label="Pack actions"
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="library-actions-slot">
                            <button
                              type="button"
                              className="row-icon-button"
                              title="Rename Pack"
                              aria-label="Rename pack"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePackRename(pack.id);
                              }}
                            >
                              <i className="fa-regular fa-pen-to-square" aria-hidden="true" />
                            </button>
                          </div>
                          <div className="library-actions-slot">
                            <button
                              type="button"
                              className="row-icon-button row-icon-button--danger"
                              title="Remove as Pack"
                              aria-label="Remove as pack"
                              onClick={(e) => {
                                e.stopPropagation();
                                removePackFromFolder(pack);
                              }}
                            >
                              <i className="fa-solid fa-trash-can" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Playlist section - only shown when a pack is selected */}
              {selectedPack && (
                <div className="library-section playlist-section" id="playlist-section" data-tour="tour-playlist">
                  <div className="section-header">
                    <h2>Playlist</h2>
                    <div className="section-header-actions">
                    <button 
                        type="button"
                        className={`add-pack-button${isSyncing ? ' syncing' : ''}`}
                        onClick={handleManualResync}
                        disabled={isSyncing}
                        title="Resync slides from Google"
                      >
                        <i className={`fa-solid fa-arrows-rotate${isSyncing ? ' fa-spin' : ''}`} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                      className="add-pack-button"
                        disabled={isPackLoading}
                        onClick={() => void handlePickPresentationsForPlaylist()}
                        title={
                          isPackLoading
                            ? 'Wait for this pack to finish loading…'
                            : 'Add presentations from Google Drive'
                        }
                      >
                        <i className="fa-solid fa-plus" aria-hidden="true" />
                    </button>
                  </div>
                  </div>
                  {addFeedback.show && addFeedback.message ? (
                    <div
                      className={`playlist-inline-feedback playlist-inline-feedback--${addFeedback.type || 'info'}`}
                      role="status"
                    >
                      {addFeedback.message}
                    </div>
                  ) : null}
                  <DndContext 
                    sensors={sensors} 
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext 
                      items={(selectedPack?.slides || []).map((s, i) => playlistSortableId(s, i))}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="library-items">
                        {isPackLoading ? (
                          <div className="playlist-loading">Loading Pack…</div>
                        ) : (
                          <>
                        <div className="drag-indicator" />
                        {renderPlaylist()}
                          </>
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>
                  <div className="playlist-footer">
                    <label className="auto-advance-toggle">
                      <span className="auto-advance-label">Auto-advance</span>
                      <button
                        type="button"
                        className={`auto-advance-switch ${autoAdvance ? 'on' : 'off'}`}
                        onClick={() => setAutoAdvance(!autoAdvance)}
                      >
                        <span className="auto-advance-knob" />
                      </button>
                    </label>
                    <button
                      type="button"
                      data-tour="tour-remote"
                      className={`remote-indicator-btn${remoteSession ? ' remote-indicator-btn--active' : ''}${remotePendingCount > 0 ? ' remote-indicator-btn--pending' : ''}`}
                      title={
                        remotePendingCount > 0
                          ? `${remotePendingCount} device${remotePendingCount > 1 ? 's' : ''} waiting for approval`
                          : remoteSession
                            ? `Remote session active — ${peerCount} connected`
                            : 'Phone / tablet remote'
                      }
                      aria-label="Phone or tablet remote"
                      onClick={() => void handleRemotePhoneClick()}
                    >
                      <i className="fa-solid fa-mobile-screen-button" aria-hidden="true" />
                      {remotePendingCount > 0 ? (
                        <span className="remote-indicator-pending">{remotePendingCount}</span>
                      ) : peerCount > 0 ? (
                        <span className="remote-indicator-count">{peerCount}</span>
                      ) : null}
                    </button>
                  </div>
                </div>
              )}
                </>
              )}
            </div>
          </div>
          
          <div className="main-content">
            <FirefoxSlidesWarningBanner />
            {tourLayoutPlaceholders ? (
              <TourPlaceholderMain previewHeightRatio={previewHeightRatio} />
            ) : !selectedPack ? (
              // Welcome screen with packs in the center when no pack is selected
              <div className="center-packs-container">
                <div className="welcome-packs-section" data-tour="tour-packs">
                  <div className="section-header">
                    <h2>Packs</h2>
                  </div>
                  <div className="welcome-packs-list">
                    {packs.map((pack) => (
                      <div key={pack.id} className="welcome-pack-row">
                        <div
                          className="welcome-pack-item welcome-pack-item--content"
                          onClick={() => handleSelectPack(pack.id)}
                        >
                          <span className="pack-name">{pack.name}</span>
                        </div>
                        <div
                          className="library-actions-section library-actions-section--two-slot welcome-pack-actions-section"
                          role="group"
                          aria-label="Pack actions"
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="library-actions-slot">
                            <button
                              type="button"
                              className="row-icon-button"
                              title="Rename Pack"
                              aria-label="Rename pack"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePackRename(pack.id);
                              }}
                            >
                              <i className="fa-regular fa-pen-to-square" aria-hidden="true" />
                            </button>
                          </div>
                          <div className="library-actions-slot">
                            <button
                              type="button"
                              className="row-icon-button row-icon-button--danger"
                              title="Remove as Pack"
                              aria-label="Remove as pack"
                              onClick={(e) => {
                                e.stopPropagation();
                                removePackFromFolder(pack);
                              }}
                            >
                              <i className="fa-solid fa-trash-can" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {packs.length === 0 && (
                      <div className="empty-packs-message">
                        <p>No packs available.</p>
                        <button 
                          className="create-first-pack-button"
                          onClick={() => setIsPacksActionModalOpen(true)}
                        >
                          Create your first pack
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              // Normal content view when a Pack is selected
              <div className="preview-notes-container" ref={previewNotesRef}>
                <div
                  className="preview-area"
                  data-tour="tour-preview"
                  style={{ flexBasis: `${previewHeightRatio * 100}%` }}
                >
                  {activeSlide ? (
                    <>
                      <div className="preview-container">
                        {/* Stack above iframe: .click-blocker blocks pointer access to Google embed (App.css). */}
                        <div className="click-blocker" aria-hidden="true" />
                        <DualGoogleSlidesEmbed
                          ref={mainSlidesEmbedRef}
                          key={activeSlide.id}
                          src={previewSlidesSrc}
                          title={`Preview: ${activeSlide.name || 'presentation'}`}
                          allowFullScreen
                          embedMuted={
                            presenterViewOpenFromStorage ||
                            (presenterWindow != null && !presenterWindow.closed) ||
                            (audienceViewOpenFromStorage && audienceDeckHasFocus)
                          }
                          onVisibleSlideReady={onMainEmbedVisibleReady}
                        />
                        {activeSlideContentLoading && (
                          <div
                            className="preview-slide-meta-loading"
                            aria-live="polite"
                            title="Thumbnails and speaker notes are still syncing from Google; the slide preview above is already on the correct slide."
                          >
                            <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                            <span>Syncing notes…</span>
                          </div>
                        )}
                      </div>
                    </>
                  ) : !isPackLoading &&
                    selectedPack &&
                    (!selectedPack.slides || selectedPack.slides.length === 0) ? (
                    <div
                      className="preview-placeholder preview-placeholder-empty-playlist"
                      role="status"
                      aria-live="polite"
                    >
                      <p className="preview-empty-playlist-title">Playlist is empty</p>
                      <p className="preview-empty-playlist-hint">
                        Click <strong>+</strong> next to Playlist to open Google Drive and pick decks, then click a
                        row in the playlist to select it.
                      </p>
                    </div>
                  ) : (
                    <div className="preview-placeholder" />
                  )}
                </div>

                <div
                  className="preview-resizer"
                  onMouseDown={() => setIsResizingPreview(true)}
                  title="Drag to resize preview and notes"
                />

                <div
                  ref={notesSectionRef}
                  className="notes-section"
                  data-tour="tour-notes"
                  style={{ flexBasis: `${(1 - previewHeightRatio) * 100}%` }}
                >
                  {activeSlide && activeSlideContentLoading ? (
                    <div className="notes-loading-placeholder">
                      <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                      <span>Loading speaker notes…</span>
                    </div>
                  ) : (
                    <div
                      ref={notesTextRef}
                      className="notes-text"
                      dangerouslySetInnerHTML={{
                        __html:
                          activeSlide && activeSlide.id
                            ? (slideContent[activeSlide.id]?.slides?.[chromeSlideIndex]?.notes || "")
                            : ''
                      }}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
          

          <div className="right-sidebar">
            <div ref={slidesPreviewScrollRef} className="slides-preview" data-tour="tour-slides-strip">
              <div className="slides-header-row">
                <h2>Slides</h2>
                <div className="slides-header-actions" data-tour="tour-open-views">
                        <button 
                          className="present-button"
                    title={activeSlide || isBlackout || isWhiteout ? "Start Audience View" : "Activate a slide deck first"}
                    disabled={!activeSlide && !isBlackout && !isWhiteout}
                          onClick={() => {
                      // Allow opening audience view even without activeSlide if blackout/whiteout is active
                      if (!activeSlide && !isBlackout && !isWhiteout) return;
                      
                      // If no active slide but blackout/whiteout is active, open without presentationId
                      // The overlay will still work via localStorage
                      const presentationId = activeSlide?.id || null;
                      const url = presentationId 
                        ? `/presentation-view?presentationId=${presentationId}`
                        : '/presentation-view';
                      try {
                        localStorage.setItem('audience-view-open', '1');
                      } catch {
                        /* ignore */
                      }
                      setAudienceViewOpenFromStorage(true);
                            const audienceWindow = window.open(
                        url,
                              'audience',
                              'width=1920,height=1080'
                            );
                      if (!audienceWindow || audienceWindow.closed) {
                        try {
                          localStorage.removeItem('audience-view-open');
                        } catch {
                          /* ignore */
                        }
                        setAudienceViewOpenFromStorage(false);
                        return;
                      }
                            setPresentationWindow(audienceWindow);
                          }}
                        >
                    <i className="fa-solid fa-play" aria-hidden="true" />
                        </button>
                        <button 
                          className="presenter-view-button"
                    title={activeSlide ? "Open Presenter View" : "Activate a slide deck first"}
                    disabled={!activeSlide}
                          onClick={() => {
                      if (!activeSlide) return;
                      const authStr = encodeShareableAuthForQuery();
                            const presenterWindow = window.open(
                              `/presenter-view?presentationId=${activeSlide.id}&auth=${authStr}`,
                              'presenter',
                              'width=1280,height=720'
                            );
                            setPresenterWindow(presenterWindow);
                          }}
                        >
                      <i className="fa-solid fa-person-chalkboard" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                        <div className="slides-list">
                {tourLayoutPlaceholders ? (
                  <TourPlaceholderSlidesStrip />
                ) : activeSlide && activeSlideContentLoading ? (
                  <div className="slides-preview-loading" aria-live="polite">
                    <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                    <span>Loading slides &amp; thumbnails…</span>
                  </div>
                ) : activeSlide && slideContent[activeSlide.id]?.slides?.length ? (
                  slideContent[activeSlide.id].slides.map((slide, index) => {
                    const shortcutCode = getDeckSlideShortcutCode(
                      selectedPack?.deckSlideShortcuts,
                      activeSlide.id,
                      slide.objectId
                    );
                    const shortcutLabel = shortcutCode ? formatShortcutBadgeLabel(shortcutCode) : '';
                    return (
                  <div 
                    key={slide.objectId}
                      className={`slide-preview-item ${index === chromeSlideIndex ? 'active' : ''}`}
                    data-tour={index === 0 ? 'tour-slide-shortcut-spotlight' : undefined}
                    onClick={() => handleSlideChange(index)}
                  >
                      <div className={`slide-thumbnail-placeholder ${!slide.thumbnail ? 'visible' : ''}`}>
                        <span className="slide-placeholder-label">Slide {slide.number}</span>
                      </div>
                      {slide.thumbnail ? (
                    <img 
                      src={slide.thumbnail} 
                      alt={`Slide ${slide.number}`}
                      referrerPolicy="no-referrer"
                      loading="lazy"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement?.querySelector('.slide-thumbnail-placeholder')?.classList.add('visible');
                          }}
                    />
                      ) : null}
                    <span className="slide-number">{slide.number}</span>
                    {shortcutLabel ? (
                      <span className="slide-shortcut-badge" title="Keyboard shortcut for this slide">
                        {shortcutLabel}
                      </span>
                    ) : null}
                    <button
                      type="button"
                      className="slide-shortcut-assign"
                      title="Assign keyboard shortcut"
                      aria-label={`Assign keyboard shortcut for slide ${slide.number}`}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!activeSlide?.id) return;
                        setSlideShortcutCapture({
                          presentationId: activeSlide.id,
                          objectId: slide.objectId,
                          slideNumber: slide.number,
                        });
                      }}
                    >
                      <i className="fa-regular fa-keyboard" aria-hidden="true" />
                    </button>
                  </div>
                    );
                  })
                ) : activeSlide ? (
                  <div className="slides-preview-empty">No slides in this deck.</div>
                ) : !isPackLoading &&
                  selectedPack &&
                  (!selectedPack.slides || selectedPack.slides.length === 0) ? (
                  <div className="slides-preview-empty">
                    Use <strong>+</strong> next to Playlist to pick decks in Drive, then select one here.
                  </div>
                ) : null}
              </div>
            </div>
            
            {/* This wrapper div is crucial for positioning */}
            <div className="controls-container">
              <div className="notification-slot">
              {notification.visible && (
                <div className={`control-notification ${notification.type}`}>
                  {notification.message}
                </div>
              )}
              </div>
              
              <div className="sidebar-controls" data-tour="tour-stage-controls">
                <button 
                  className="control-button primary" 
                  title={(presenterWindow && !presenterWindow.closed) || presenterViewOpenFromStorage ? "Reset Timer (T)" : "Open Presenter View to use timer"}
                  disabled={!((presenterWindow && !presenterWindow.closed) || presenterViewOpenFromStorage)}
                  onClick={handleTimerReset}
                >
                  <i className="fa-regular fa-clock control-icon" aria-hidden="true" />
                </button>
                <button 
                  className={`control-button ${isBlackout ? 'active' : ''}`}
                  title="Blackout (B)"
                  onClick={handleBlackout}
                >
                  <i className="fa-regular fa-square control-icon" aria-hidden="true" />
                </button>
                <button 
                  className={`control-button ${isWhiteout ? 'active' : ''}`}
                  title="Whiteout (W)"
                  onClick={handleWhiteout}
                >
                  <i className="fa-solid fa-square control-icon" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>


          {/* Packs action modal: create new pack or use existing folder */}
          {isPacksActionModalOpen && (
            <div className="modal-overlay" onClick={() => setIsPacksActionModalOpen(false)}>
              <div className="packs-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Create or Select Pack</h2>
                  <button 
                    className="close-modal-button"
                    onClick={() => setIsPacksActionModalOpen(false)}
                  >
                    <i className="fa-solid fa-xmark" aria-hidden="true" />
                  </button>
                </div>
                <div className="packs-action-list">
                      <button 
                    className="packs-action-option"
                    onClick={handleCreatePackFromModal}
                      >
                    <span className="packs-action-icon"><i className="fa-solid fa-plus" aria-hidden="true" /></span>
                    <span className="packs-action-text">Create new Pack (new folder)</span>
                      </button>
                  <button type="button" className="packs-action-option" onClick={() => void handlePickFolderForPack()}>
                    <span className="packs-action-icon"><i className="fa-regular fa-folder-open" aria-hidden="true" /></span>
                    <span className="packs-action-text">Choose a Drive folder as Pack…</span>
                  </button>
                </div>
                          </div>
                        </div>
                      )}
                      
          {slideShortcutCapture ? (
            <div
              ref={slideShortcutCaptureFocusRef}
              tabIndex={-1}
              className="slide-shortcut-capture-bar"
              role="status"
              aria-live="polite"
            >
              <span className="slide-shortcut-capture-bar__text">
                Press a key for slide {slideShortcutCapture.slideNumber}.{' '}
                <kbd>Backspace</kbd> clears · <kbd>Esc</kbd> cancels
              </span>
                    </div>
          ) : null}

          <RemoteOperatorModal
            open={remoteOperatorModalOpen}
            onClose={() => {
              setRemotePhoneStartError('');
              setRemoteOperatorModalOpen(false);
            }}
            initialSession={remoteSession}
            idleErrorHint={remotePhoneStartError}
            onSession={handleRemoteOperatorSession}
            onBeforeEndSession={() => broadcastSessionEnd.current?.()}
            onDisconnectPeer={(clientId) => disconnectPeer.current?.(clientId)}
            onApprovePeer={(clientId, token) => sendApproval.current?.(clientId, token)}
            onJoinNotifySettingsChange={() => setJoinAlertPrefEpoch((e) => e + 1)}
          />
          <OnboardingTour active={tourOpen} onDismiss={() => setTourOpen(false)} />
                    </div>
                  )}
      <AppDialog dialog={appDialog} onResolve={resolveAppDialog} />
    </div>
  );
}

export default App;

